'use client';
import { buildPlanningPlanSelectionHref } from '@/app/planning/planningNavigation';

import announcedCourses from '@/data/announced_courses.json';
import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '@/app/Components/AuthProvider';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { degreePlansApi, usersApi, courseListsApi, degreesApi } from '@/app/services/api';
import {
  normalize,
  isLikelyCourseCode,
  DegreeRequirementService
} from '@/app/services/degreeRequirementService';
import {
  calculateDegreeProgress as calculateDegreeProgressService,
  createCalculationContext
} from '@/app/services/degreeCalculationService';
import {
  loadDegreeCourseListsDirect as loadDegreeCourseListsDirectData,
  loadDegreeRequirementsById
} from '@/app/services/degreeDataService';
import { loadUserAcademicData, buildUserPrerequisiteInfo } from '@/app/services/userAcademicData';
import Modal from '@/app/Components/Modal';
import { DegreePlanningModal } from './components/DegreePlanningTransient';
import { AcademicDecisionFeedback, type AcademicDecisionFeedbackState } from '@/app/Components/OperationalPage';
import { primaryDegreePlanStorageKey, resolvePrimaryDegreePlanId } from './primaryPlanPreference';
import { LoadingBubbles, PageLoading } from '@/app/Components/LoadingBubbles';
import { getCatalogSeasonFlags } from '@/app/lib/classesCatalogSeasons';
import {
  AcademicCapIcon,
  PlusIcon,
  BookOpenIcon,
  CheckIcon,
  XMarkIcon,
  
  ShareIcon,
  HeartIcon,
  MusicalNoteIcon,
  BuildingOfficeIcon,
  BeakerIcon,
  UserGroupIcon,
  CogIcon,
  ChartBarIcon,
  SparklesIcon,
  ChevronDownIcon,
  
  
  TrashIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useCatalogCourses } from "@/app/services/useCatalogCourses";
import { checkPrerequisites, getRecursivePrerequisites } from '@/app/services/prerequisiteChecker';
import { SCHOOL_DEGREE_IDS } from '@/app/lib/constants';

import CourseSearch from '@/app/Components/CourseSearch';
import { GroupedCourse } from '@/app/types/course';
import { RutgersCourse } from '@/app/types/RutgersCourse';
import type { CoreCode } from '@/app/services/courseCatalogShared';
import type { DegreePlan, Course, Semester, Degree, DegreeData, SemesterItem } from '@/app/types/interfaces';
import { useOnboardingModal } from '@/app/hooks/useOnboardingModal';
import AcademicProgramBadge from '@/app/Components/AcademicProgramBadge';
import OnboardingModal from '@/app/Components/OnboardingModal';
import PrerequisiteCheckerPrompt from '@/app/Components/PrerequisiteCheckerPrompt';
import { supabase } from '@/app/lib/supabase';
import { generateAutoFillPlan } from '@/app/services/degreeAutoFillEngine';
import PlannerWorkspace from '@/app/degree-planning/planner/PlannerWorkspace';
import { applyProposal, diffProposal, type ProposalTerm, type ProposedTermInput } from '@/app/degree-planning/planner/planProposal';
import { userListMappingName, type UserCourseList } from '@/app/degree-planning/planner/plannerContext';
import PlannerFixture from '@/app/degree-planning/planner/PlannerFixture';
import DegreeTargets from '@/app/degree-planning/components/DegreeTargets';
import AssistantButton from '@/app/Components/Assistant/AssistantButton';
import { getSafeRedirectPath } from '@/app/services/authHelpers';
import { resolveCoreJourneyReleaseFixture } from '@/app/lib/coreJourneyReleaseFixture';
import { auditFixtureState } from '@/app/lib/routeStateContract';
import { CoreJourneyReleaseFixture } from '@/app/Components/CoreJourneyReleaseFixture';
import { programColor } from './planner/plannerModel';

// Local interface for degree planning page (different from API interface)
interface DegreePlanData {
  name: string;
  major: string;
  description?: string;
  totalCredits: number;
  targetGraduation: string;
  semesters: Semester[];
  requirements: {
    coreCredits: number;
    majorCredits: number;
    electiveCredits: number;
    completed: number;
  };
  selectedDegreeIds?: number[];
}


// School icon mapping
const SCHOOL_ICONS: { [key: string]: any } = {
  'School of Nursing': HeartIcon,
  'Honors College': SparklesIcon,
  'Douglass Residential College': UserGroupIcon,
  'Mason Gross School of the Arts': MusicalNoteIcon,
  'School of Engineering': CogIcon,
  'Edward J. Bloustein School': BuildingOfficeIcon,
  'Rutgers Business School': ChartBarIcon,
  'School of Management and Labor Relations': BuildingOfficeIcon,
  'School of Environmental and Biological Sciences': BeakerIcon,
  'The SAS Core Curriculum': AcademicCapIcon,
  'Honors Program': SparklesIcon,
  'Honors College-SEBS': SparklesIcon,
  'Honors College-MGSA': SparklesIcon,
  'Honors College-SOE': SparklesIcon,
  'Honors College-SAS': SparklesIcon,
  'Honors College-PHARM': SparklesIcon,
  'Honors Program -SEBS': SparklesIcon,
  'Honors Program -SAS': SparklesIcon
};

// Function to get icon for a school
const getSchoolIcon = (schoolName: string) => {
  for (const [key, icon] of Object.entries(SCHOOL_ICONS)) {
    if (schoolName.includes(key)) {
      return icon;
    }
  }
  return AcademicCapIcon; // Default icon
};

// Function to categorize schools
const categorizeSchools = (degrees: any[]) => {
  const schools = degrees.filter(d => SCHOOL_DEGREE_IDS.includes(d.ID));
  const honors = schools.filter(d =>
    d.Name.includes('Honors') ||
    d.Name.includes('Douglass Residential College')
  );
  const regularSchools = schools.filter(d =>
    !d.Name.includes('Honors') &&
    !d.Name.includes('Douglass Residential College')
  );

  return { schools: regularSchools, honors };
};

// Predefined color palette for degrees (using Tailwind-compatible colors)
const DEGREE_PLANNING_DEBUG = process.env.NEXT_PUBLIC_DEBUG_DEGREE_PLANNING === 'true';

const debugDegreePlanning = (...args: unknown[]) => {
  if (DEGREE_PLANNING_DEBUG) {
    console.debug(...args);
  }
};

// Function to generate consistent color from degree name

// Helper to format course code: 01198101 -> 01:198:101
const formatCourseCode = (code: string) => {
  const digits = String(code).replace(/[^0-9]/g, '');
  if (digits.length >= 7) {
    return `${digits.slice(0, 2)}:${digits.slice(2, 5)}:${digits.slice(5)}`;
  }
  return code;
};

function DegreePlanningPage() {
  return <DegreePlanningWorkspace />;
}

export function DegreePlanningWorkspace({ embedded = false, openTransferOptions = false }: { embedded?: boolean; openTransferOptions?: boolean }) {
  const searchParams = useSearchParams();
  const shouldOpenTransferOptions = openTransferOptions || searchParams.get('transfer') === '1';
  const fixture = resolveCoreJourneyReleaseFixture('degree-planning', searchParams.get('auditFixture'));
  // The planner views fixture runs in development and in the fixture-only release-gate build.
  if (searchParams.get('auditFixture') === 'degree-planner-views' && (process.env.NODE_ENV !== 'production' || auditFixtureState('populated') === 'populated')) return <PlannerFixture />;
  return fixture ? <CoreJourneyReleaseFixture route="degree-planning" /> : <>
    {shouldOpenTransferOptions ? <div role="note" className="mx-auto mb-4 max-w-[1860px] rounded-card border border-line bg-surface-subtle px-4 py-3 text-sm text-content"><strong>Transfer credit:</strong> compare published NJ Transfer evaluations by receiving school, alongside the curated Rutgers Math chart. Coverage and conditions are shown with each option; confirm current approval and course details with Rutgers.</div> : null}
    <DegreePlanningRuntime embedded={embedded} openTransferOptions={shouldOpenTransferOptions} />
  </>;
}

function DegreePlanningRuntime({ embedded = false, openTransferOptions = false }: { embedded?: boolean; openTransferOptions?: boolean }) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { showModal: showOnboardingModal, closeModal: closeOnboardingModal } = useOnboardingModal();
  const [degreePlans, setDegreePlans] = useState<DegreePlan[]>([]);
  const [degreePlansOwnerId, setDegreePlansOwnerId] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<DegreePlan | null>(null);
  const [primaryPlanId, setPrimaryPlanId] = useState<number | null>(null);
  const [planData, setPlanData] = useState<DegreePlanData | null>(null);
  const [planSelectionLoading, setPlanSelectionLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanMajor, setNewPlanMajor] = useState('');
  const [newPlanDegreeIds, setNewPlanDegreeIds] = useState<number[]>([]);
  const [newPlanTargetGraduation, setNewPlanTargetGraduation] = useState('Spring 2028');
  const [newPlanDescription, setNewPlanDescription] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [onboardingMajors, setOnboardingMajors] = useState<string[]>([]);
  // GPA/previous classes UI removed from this page (moved to /gpa-calculator)

  const [showAddToSemesterModal, setShowAddToSemesterModal] = useState(false);
  const [selectingCourseList, setSelectingCourseList] = useState<{ item: any, semesterNumber: number, itemIndex: number } | null>(null);
  const [courseListSearch, setCourseListSearch] = useState('');
  const [targetSemester, setTargetSemester] = useState<number | null>(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editedPlan, setEditedPlan] = useState<{
    name: string;
    major: string;
    targetGraduation: string;
    description?: string;
    selectedDegreeIds?: number[];
  } | null>(null);

  const [dummySelectedCourses] = useState<GroupedCourse[]>([]);
  const [dummyCoreBlocks] = useState<{ coreCode: CoreCode; selectedCourse?: RutgersCourse }[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [planningFeedback, setPlanningFeedback] = useState<{ title: string; description: string; kind: Extract<AcademicDecisionFeedbackState, 'error' | 'saved' | 'stale'> } | null>(null);
  const [userPrereqInfo, setUserPrereqInfo] = useState<any[]>([]);
  const [courseListCache] = useState<Map<number, { name: string, classes: string[] }>>(new Map());
  // Removed staticCourseListCache - now using degreeCourseListsDirect only
  const [courseNameMap, setCourseNameMap] = useState<Map<string, string>>(new Map());
  const catalogCourses = useCatalogCourses();
  // Extract search param values as strings to avoid serialization issues
  // Get values directly without putting searchParams in dependency arrays
  const importDegreeId = searchParams?.get('importDegreeId') || null;
  const planIdFromUrl = searchParams?.get('planId') || null;
  const pendingCourseId = searchParams?.get('addCourse') || null;
  const pendingCourseAddHandledRef = useRef<string | null>(null);

  // Create a stable string representation of search params for dependency tracking
  const searchParamsString = `${importDegreeId || ''}-${planIdFromUrl || ''}-${pendingCourseId || ''}`;
  const planSelectionHref = (planId: string | number) => {
    if (!embedded) return `/degree-planning?planId=${planId}`;
    return buildPlanningPlanSelectionHref(planId, new URLSearchParams(typeof window === 'undefined' ? searchParams.toString() : window.location.search));
  };
  const primaryPlanStorageKey = user?.id ? primaryDegreePlanStorageKey(user.id) : null;

  // The primary plan is a convenience preference stored on this device. The URL
  // remains authoritative when a plan is explicitly selected or shared.
  useEffect(() => {
    if (!user?.id || degreePlansOwnerId !== user.id || degreePlans.length === 0) {
      setPrimaryPlanId(null);
      return;
    }
    const savedValue = (() => {
      try { return window.localStorage.getItem(primaryDegreePlanStorageKey(user.id)); }
      catch { return null; }
    })();
    const chosen = resolvePrimaryDegreePlanId(degreePlans, savedValue);
    if (chosen == null) return;
    setPrimaryPlanId(chosen);
    try { window.localStorage.setItem(primaryDegreePlanStorageKey(user.id), String(chosen)); } catch { /* storage is optional */ }
  }, [user?.id, degreePlansOwnerId, degreePlans]);
  const routeRedirectTarget = useMemo(() => {
    const query = searchParams?.toString();
    return getSafeRedirectPath(`${pathname}${query ? `?${query}` : ''}`);
  }, [pathname, searchParams]);

  const creditsMap = useMemo(() => {
    const map = new Map<string, number>();
    const courses = catalogCourses;
    courses.forEach((c: any) => {
      const code = normalize(c.baseId || c.id || '');
      if (code) {
        const cr = typeof c.credits === 'string' ? parseFloat(c.credits) : Number(c.credits);
        if (Number.isFinite(cr)) map.set(code, cr);
      }
    });
    return map;
  }, [catalogCourses]);

  // Degree search integration
  const [availableDegrees, setAvailableDegrees] = useState<Degree[]>([]);
  const [showDegreeImportModal, setShowDegreeImportModal] = useState(false);
  const [showSchoolRequirementsModal, setShowSchoolRequirementsModal] = useState(false);
  // Removed degreeMappings - now using degreeCourseListsDirect only
  const [degreeCourseListsDirect, setDegreeCourseListsDirect] = useState<Map<string, { classes: string[]; name?: string; description?: string }>>(new Map());
  const [degreeRequirementsCache, setDegreeRequirementsCache] = useState<Map<number, DegreeData>>(new Map());
  const [degreeSearchQuery, setDegreeSearchQuery] = useState('');
  const filteredDegrees = useMemo(() => {
    const q = degreeSearchQuery.trim().toLowerCase();
    if (!q) return availableDegrees;
    return availableDegrees.filter(d =>
      (d.Name?.toLowerCase() || '').includes(q) || (d["Degree Code"]?.toLowerCase() || '').includes(q)
    );
  }, [degreeSearchQuery, availableDegrees]);
  // Requirements rendering state (collapsible UI)
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [showSummerWinterOnly, setShowSummerWinterOnly] = useState(false);
  const [togglingShare, setTogglingShare] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Hierarchical requirements state (like degree-search)
  const [selectedDegreeRequirements, setSelectedDegreeRequirements] = useState<any[]>([]);
  const [degreeProgress, setDegreeProgress] = useState<any | null>(null);
  const [listsLoadedVersion, setListsLoadedVersion] = useState(0);
  const [showPrerequisitePrompt, setShowPrerequisitePrompt] = useState(false);
  const [isAutofilling, setIsAutofilling] = useState(false);
  // Auto-fill output awaiting review. Only an accept writes it to the plan.
  const [proposedTerms, setProposedTerms] = useState<ProposedTermInput[] | null>(null);
  const [isAcceptingProposal, setIsAcceptingProposal] = useState(false);
  const [userCourseLists, setUserCourseLists] = useState<UserCourseList[]>([]);
  const [userCourseListsReady, setUserCourseListsReady] = useState<boolean | null>(null);
  const [showSeasonSelectorModal, setShowSeasonSelectorModal] = useState(false);
  const [seasonToAdd, setSeasonToAdd] = useState<'Spring' | 'Summer' | 'Fall' | 'Winter'>('Fall');
  const [yearToAdd, setYearToAdd] = useState<number>(new Date().getFullYear());

  // Caching and de-dupe for network calls
  // Removed degreeMappingsFetchedRef - no longer needed
  const userDataLoadedRef = useRef<string | null>(null);
  const userDataRequestRef = useRef(0);
  const planSelectionRequestRef = useRef(0);
  const currentAuthUserIdRef = useRef<string | null>(user?.id ?? null);
  currentAuthUserIdRef.current = user?.id ?? null;
  const degreesLoadedRef = useRef(false);
  const userAcademicDataLoadedRef = useRef(false);

  const plannedCodes = useMemo(() => (planData?.semesters || []).flatMap(s =>
    (Array.isArray((s as any).courses) ? (s as any).courses : [])
      .filter((it: any) => it?.kind === 'course' || (it?.code && !it.kind))
      .map((it: any) => normalize(String(it.code)))
  ), [planData?.semesters]);

  const plannedCourseListBlocks = useMemo(() => (planData?.semesters || []).flatMap(s =>
    (Array.isArray((s as any).courses) ? (s as any).courses : [])
      .filter((it: any) => it?.kind === 'course_list')
      .map((it: any) => it.mappingName)
  ), [planData?.semesters]);

  // GPA removed

  // Close plan selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showPlanSelector) {
        const target = e.target as HTMLElement;
        if (!target.closest('.plan-selector-container')) {
          setShowPlanSelector(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPlanSelector]);

  // Check if user is authenticated
  useEffect(() => {
    if (!loading && !user) {
      router.replace(`/login?redirect=${encodeURIComponent(routeRedirectTarget)}`);
    }
  }, [user, loading, routeRedirectTarget, router]);

  // Onboarding Modal - will be rendered at the end of the component

  // Saved course lists for "Your lists" in the planner's Add and slot flows.
  useEffect(() => {
    if (!user?.id) { setUserCourseLists([]); setUserCourseListsReady(loading ? null : false); return; }
    let active = true;
    setUserCourseListsReady(null);
    courseListsApi.get_all_user_course_lists(user.id)
      .then((lists: Array<{ id: string | number; name?: string; classes?: string[] }>) => {
        if (!active) return;
        setUserCourseLists((lists || []).map((list) => ({
          id: String(list.id),
          name: list.name || 'Untitled list',
          classes: Array.from(new Set((list.classes || []).map((code) => normalize(code)).filter(Boolean))),
        })).filter((list) => list.classes.length > 0));
        setUserCourseListsReady(true);
      })
      .catch((error: unknown) => {
        console.error('Error loading saved course lists:', error);
        if (active) setUserCourseListsReady(false);
      });
    return () => { active = false; };
  }, [user?.id, loading]);

  useEffect(() => {
    userDataRequestRef.current += 1;
    planSelectionRequestRef.current += 1;
    userDataLoadedRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (degreePlansOwnerId === user?.id) return;
    if (!degreePlans.length && !selectedPlan && !planData) return;
    setDegreePlans([]);
    setDegreePlansOwnerId(null);
    setSelectedPlan(null);
    setPlanData(null);
    setPrimaryPlanId(null);
  }, [degreePlansOwnerId, degreePlans.length, planData, selectedPlan, user?.id]);

  // A pending auto-fill proposal belongs to the plan it was drafted for.
  useEffect(() => { setProposedTerms(null); }, [selectedPlan?.id]);

  // Load user data and degree plans
  useEffect(() => {
    if (user?.id && userDataLoadedRef.current !== user.id) {
      loadUserData();
      userDataLoadedRef.current = user.id;
    }
  }, [user?.id]);

  // Load available degrees (only once)
  useEffect(() => {
    if (!degreesLoadedRef.current) {
      loadAvailableDegrees();
      degreesLoadedRef.current = true;
    }
  }, []);

  // Load static course lists and degree course list mappings (only once)
  useEffect(() => {
    const initData = async () => {
      await refreshDegreeCourseListsDirect(); // Load direct name -> classes mapping from shared service
    };
    initData();
  }, []);

  // Handle importDegreeId query param to auto-import a degree selected from Degree Search page
  useEffect(() => {
    const idStr = searchParams?.get('importDegreeId');
    if (!idStr || loading || !user) return;

    const id = Number(idStr);
    if (Number.isNaN(id)) return;

    if (!selectedPlan && degreePlans.length === 0 && !showCreateForm) {
      setNewPlanDegreeIds(prev => Array.from(new Set([...prev, id])));
      setNewPlanMajor(onboardingMajors.join(', '));
      setShowCreateForm(true);
      const newUrl = window.location.pathname + window.location.search.replace(/&?importDegreeId=\d+/, '');
      window.history.replaceState({}, '', newUrl);
      return;
    }

    if (!selectedPlan && degreePlans.length > 0) return;

    handleImportDegreeRequirements(id);
    const newUrl = window.location.pathname + window.location.search.replace(/&?importDegreeId=\d+/, '');
    window.history.replaceState({}, '', newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString, loading, user, selectedPlan, degreePlans.length, showCreateForm, onboardingMajors]);

  // Auto-select plan based on URL ?planId, and load its data
  useEffect(() => {
    const planIdStr = searchParams?.get('planId');

    const selectPlanById = async (id: number) => {
      const plan = degreePlans.find(p => p.id === id);
      // Only update state if the plan is different from the currently selected one
      if (plan && (!selectedPlan || selectedPlan.id !== id)) {
        const request = ++planSelectionRequestRef.current;
        const ownerId = user?.id;
        setPlanSelectionLoading(true);
        try {
          setSelectedPlan(plan);
          const fullPlan = await degreePlansApi.get(id);
          if (request !== planSelectionRequestRef.current || currentAuthUserIdRef.current !== ownerId || degreePlansOwnerId !== ownerId) return;
          const planData = fullPlan?.plan || null;
          setPlanData(planData);
          if (planData) {
            setEditedPlan({
              name: planData.name || plan.name || '',
              major: planData.major || '',
              targetGraduation: planData.targetGraduation || '',
              description: planData.description || ''
            });
          }
        } finally {
          if (request === planSelectionRequestRef.current) setPlanSelectionLoading(false);
        }
      }
    };

    if (degreePlansOwnerId === user?.id && degreePlans.length > 0) {
      if (planIdStr) {
        const pid = Number(planIdStr);
        if (!Number.isNaN(pid)) {
          if (degreePlans.some((plan) => plan.id === pid)) selectPlanById(pid);
          else if (embedded) window.history.replaceState(null, '', planSelectionHref(primaryPlanId ?? degreePlans[0].id));
          else router.replace(planSelectionHref(primaryPlanId ?? degreePlans[0].id));
        }
      } else if (primaryPlanId != null) {
        // Use the device-local primary only when the URL does not name a plan.
        if (embedded) window.history.replaceState(null, '', planSelectionHref(primaryPlanId));
        else router.replace(planSelectionHref(primaryPlanId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString, degreePlans, degreePlansOwnerId, selectedPlan, primaryPlanId, router, user?.id]);

  // Load direct mapping from degree_course_lists_direct.csv through the shared degree data service.
  const refreshDegreeCourseListsDirect = async () => {
    try {
      const directMap = await loadDegreeCourseListsDirectData();
      setDegreeCourseListsDirect(directMap);
      debugDegreePlanning(`[refreshDegreeCourseListsDirect] Loaded ${directMap.size} direct course list mappings`);
      return directMap;
    } catch (e) {
      console.error('[refreshDegreeCourseListsDirect] Error loading direct course list mappings:', e);
      return new Map<string, { classes: string[]; name?: string; description?: string }>();
    }
  };

  // Load unified academic data for prior classes (for requirement matching only)
  useEffect(() => {
    const hydratePrevious = async () => {
      if (!user?.id || userAcademicDataLoadedRef.current) return;
      try {
        const data = await loadUserAcademicData(user.id);
        if (!data) return;
        const unified = await buildUserPrerequisiteInfo(data);
        setUserPrereqInfo(unified);
        userAcademicDataLoadedRef.current = true;

        // Check if user has completed courses and show prompt if not
        if (!data.completedCourses || data.completedCourses.length === 0) {
          setShowPrerequisitePrompt(true);
        }
      } catch (e) {
        console.error('Failed to hydrate previous classes:', e);
      }
    };
    hydratePrevious();
  }, [user?.id]);

  // Build course name map
  useEffect(() => {
    (async () => {
      const courses = catalogCourses;
      const map = new Map<string, string>();
      courses.forEach((c: any) => {
        map.set(c.baseId, c.courseName);
        map.set(c.id, c.courseName);
        map.set(c.baseId.replace(/^0+/, ''), c.courseName);
      });
      setCourseNameMap(map);
    })();
  }, [catalogCourses]);

  // helpers adopted from degree-search for code normalization/mappings
  // Semester helpers
  const CURRENT_START = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    if (month === 0) return { season: 'Winter' as const, year };
    if (month >= 1 && month <= 4) return { season: 'Spring' as const, year };
    if (month >= 5 && month <= 7) return { season: 'Summer' as const, year };
    return { season: 'Fall' as const, year };
  }, []);
  const parseSemesterName = (name: string): { season: 'Spring' | 'Summer' | 'Fall' | 'Winter'; year: number } | null => {
    const m = /(Spring|Summer|Fall|Winter)\s+(\d{4})/i.exec(name || '');
    if (!m) return null;
    const s = m[1].toLowerCase();
    const season = s === 'spring' ? 'Spring' : s === 'summer' ? 'Summer' : s === 'fall' ? 'Fall' : 'Winter';
    const year = Number(m[2]);
    return { season: season as any, year };
  };
  const formatSemesterName = (season: 'Spring' | 'Summer' | 'Fall' | 'Winter', year: number) => `${season} ${year}`;
  const compareSemesters = (a: string, b: string): number => {
    const pa = parseSemesterName(a) || CURRENT_START;
    const pb = parseSemesterName(b) || CURRENT_START;
    if (pa.year !== pb.year) return pa.year - pb.year;
    // Winter -> Spring -> Summer -> Fall
    const seasonOrder = { 'Winter': 1, 'Spring': 2, 'Summer': 3, 'Fall': 4 };
    return seasonOrder[pa.season] - seasonOrder[pb.season];
  };
  const getNextSemesterName = (name?: string): string => {
    const base = name ? parseSemesterName(name) : CURRENT_START;
    const season = base?.season || CURRENT_START.season;
    const year = base?.year || CURRENT_START.year;
    if (season === 'Winter') return formatSemesterName('Spring', year);
    if (season === 'Spring') return formatSemesterName('Summer', year);
    if (season === 'Summer') return formatSemesterName('Fall', year);
    return formatSemesterName('Winter', year + 1);
  };

  // GPA removed

  // Update page title
  useEffect(() => {
    document.title = 'Degree Planning - Scarlet Sync';
  }, []);

  const loadUserData = async () => {
    if (!user?.id) return;
    const request = ++userDataRequestRef.current;
    const requestedUserId = user.id;
    const isCurrentRequest = () => request === userDataRequestRef.current && currentAuthUserIdRef.current === requestedUserId;
    try {
      setLoadingError(null);
      const data = await usersApi.get(requestedUserId);
      if (!isCurrentRequest()) return;
      setCurrentUser(data[0]);

      const { data: onboardingData } = await supabase.functions.invoke('onboarding', {
        body: { action: 'get' }
      });
      if (!isCurrentRequest()) return;

      if (onboardingData && onboardingData.majors) {
        setOnboardingMajors(onboardingData.majors);
      }

      const plans = await degreePlansApi.getAllUserPlans(requestedUserId);
      if (!isCurrentRequest()) return;
      setDegreePlans(plans);
      setDegreePlansOwnerId(requestedUserId);
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Error loading user data:', error);
      setLoadingError('We could not load your degree plans. Try again in a moment.');
    } finally {
      if (isCurrentRequest()) setIsLoading(false);
    }
  };

  const loadAvailableDegrees = async () => {
    try {
      const degrees = await degreesApi.getAll();
      setAvailableDegrees(degrees);
    } catch (error) {
      console.error('Error loading available degrees:', error);
    }
  };

  const handleImportDegreeRequirements = async (degreeId: number) => {
    try {
      if (showCreateForm) {
        setNewPlanDegreeIds(prev => Array.from(new Set([...prev, degreeId])));
        setShowDegreeImportModal(false);
        return;
      }

      if (isEditingPlan && editedPlan) {
        setEditedPlan(prev => prev ? {
          ...prev,
          selectedDegreeIds: Array.from(new Set([...(prev.selectedDegreeIds || []), degreeId]))
        } : null);
        setShowDegreeImportModal(false);
        return;
      }

      if (!selectedPlan || !planData) {
        setShowDegreeImportModal(false);
        setShowSchoolRequirementsModal(false);
        setPlanningFeedback({ title: 'Select a degree plan first', description: 'Create or select a plan before adding program requirements.', kind: 'stale' });
        return;
      }
      const updated = { ...planData } as DegreePlanData;
      const ids = new Set<number>(updated.selectedDegreeIds || []);
      ids.add(degreeId);
      updated.selectedDegreeIds = Array.from(ids);
      await degreePlansApi.update(selectedPlan.id, { plan: updated });
      setPlanData(updated);
      setShowDegreeImportModal(false);
      setShowSchoolRequirementsModal(false);
    } catch (error) {
      console.error('Error adding degree to plan:', error);
      setPlanningFeedback({ title: 'Requirements could not be added', description: 'Please try adding the program requirements again.', kind: 'error' });
    }
  };

  // removed: convertDegreeDataToPlan (we no longer overwrite plan when importing)

  // computeRemainingFromMappings removed

  const loadDegreePlans = async (userId: string) => {
    try {
      // Get all degree plans for the user using the new edge function action
      const userPlans = await degreePlansApi.getAllUserPlans(userId);
      setDegreePlans(userPlans || []);
      setDegreePlansOwnerId(userId);

      // URL selection or the device-local primary preference chooses the active plan.

    } catch (error) {
      console.error('Error loading degree plans:', error);
    }
  };

  const handleCreatePlan = async () => {
    if (!currentUser || !newPlanName.trim()) return;

    try {
      setIsCreating(true);

      const defaultPlanData: DegreePlanData = {
        name: newPlanName.trim(),
        major: newPlanMajor.trim(), // Keep for now as major is likely expected by API
        description: newPlanDescription.trim(),
        totalCredits: 120,
        targetGraduation: newPlanTargetGraduation.trim() || 'Spring 2028',
        semesters: [
          {
            number: 1,
            name: formatSemesterName(CURRENT_START.season as any, CURRENT_START.year),
            courses: [],
            totalCredits: 0
          },
          {
            number: 2,
            name: getNextSemesterName(formatSemesterName(CURRENT_START.season as any, CURRENT_START.year)),
            courses: [],
            totalCredits: 0
          }
        ],
        requirements: {
          coreCredits: 40,
          majorCredits: 60,
          electiveCredits: 20,
          completed: 0
        },
        selectedDegreeIds: newPlanDegreeIds
      };

      const newPlan = await degreePlansApi.create({
        p_name: newPlanName.trim(),
        p_plan: defaultPlanData,
        p_is_shareable: false
      });

      // Refresh plans and redirect to the new plan
      await loadDegreePlans(currentUser.uid);
      setShowCreateForm(false);
      setNewPlanName('');
      setNewPlanMajor('');
      const createdPlan = { id: newPlan.id, ...newPlan } as any;
      setSelectedPlan(createdPlan);
      setPlanData(defaultPlanData);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('planId', String(newPlan.id));
        router.replace(`${url.pathname}?${url.searchParams.toString()}`);
      } catch { }

    } catch (error) {
      console.error('Error creating degree plan:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectPlan = async (plan: DegreePlan) => {
    // This function is now just for navigation.
    if (embedded) window.history.pushState(null, '', planSelectionHref(plan.id));
    else router.push(planSelectionHref(plan.id));
  };

  useEffect(() => {
    const handleAssistantDegreePlanUpdate = async (event: Event) => {
      if (!user?.id) return;

      const customEvent = event as CustomEvent<{ planId?: number }>;
      const updatedPlanId = customEvent.detail?.planId;

      try {
        setPlanSelectionLoading(true);
        const plans = await degreePlansApi.getAllUserPlans(user.id);
        setDegreePlans(plans || []);

        const targetPlanId = updatedPlanId ?? selectedPlan?.id;
        if (!targetPlanId) return;

        const matchingPlan = (plans || []).find((plan: any) => Number(plan.id) === Number(targetPlanId));
        if (matchingPlan) {
          setSelectedPlan(matchingPlan);
        }

        const fullPlan = await degreePlansApi.get(Number(targetPlanId));
        const nextPlanData = fullPlan?.plan || null;
        setPlanData(nextPlanData);

        if (nextPlanData) {
          setEditedPlan({
            name: nextPlanData.name || matchingPlan?.name || '',
            major: nextPlanData.major || '',
            targetGraduation: nextPlanData.targetGraduation || '',
            description: nextPlanData.description || ''
          });
        }
      } catch (error) {
        console.error('Error refreshing assistant-updated degree plan:', error);
      } finally {
        setPlanSelectionLoading(false);
      }
    };

    window.addEventListener('assistant:degree-plan-updated', handleAssistantDegreePlanUpdate as EventListener);
    return () => {
      window.removeEventListener('assistant:degree-plan-updated', handleAssistantDegreePlanUpdate as EventListener);
    };
  }, [user?.id, selectedPlan?.id]);

  const addItemToSemester = async (semesterNumber: number, item: SemesterItem) => {
    if (!selectedPlan || !planData) return false;

    try {
      const updatedPlanData = { ...planData };
      const semester = updatedPlanData.semesters.find(s => s.number === semesterNumber);

      if (semester) {
        // initialize courses array if needed
        if (!Array.isArray(semester.courses)) semester.courses = [] as any;
        (semester.courses as any).push(item);
        if ((item as any).kind === 'course') {
          const c = item as Extract<SemesterItem, { kind: 'course' }>;
          semester.totalCredits += c.credits;
          updatedPlanData.requirements.completed += c.credits;
        }
      }

      await degreePlansApi.update(selectedPlan.id, { plan: updatedPlanData });
      setPlanData(updatedPlanData);
      return true;

    } catch (error) {
      console.error('Error adding course:', error);
      return false;
    }
  };

  const addCourseToFirstEmptySemester = async (code: string) => {
    if (!selectedPlan || !planData) return false;

    // Find the first semester with less than 5 courses (simple heuristic for "empty" or "has space")
    const semesters = planData.semesters || [];
    let targetSemester = semesters[0]?.number ?? 1;

    for (let i = 1; i <= 8; i++) {
      const sem = semesters.find((s: any) => s.number === i);
      if (!sem || (sem.courses || []).length < 5) {
        targetSemester = i;
        break;
      }
    }

    const name = getCourseName(code);
    const credits = getCourseCredits(code) || 3;

    const item: SemesterItem = {
      kind: 'course',
      code,
      name,
      credits,
      semester: targetSemester,
      completed: false,
    };

    return addItemToSemester(targetSemester, item);
  };

  useEffect(() => {
    if (!pendingCourseId || !selectedPlan || !planData || pendingCourseAddHandledRef.current === pendingCourseId) return;
    const normalizedPending = normalize(pendingCourseId);
    if (!normalizedPending) return;
    pendingCourseAddHandledRef.current = pendingCourseId;
    const clearPendingCourse = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('addCourse');
      router.replace(`${url.pathname}${url.search ? url.search : ''}`);
    };
    if (plannedCodes.includes(normalizedPending)) {
      setPlanningFeedback({ title: 'Course already planned', description: `${formatCourseCode(normalizedPending)} is already in ${selectedPlan.name}.`, kind: 'stale' });
      clearPendingCourse();
      return;
    }
    void addCourseToFirstEmptySemester(formatCourseCode(normalizedPending)).then((added) => {
      setPlanningFeedback(added
        ? { title: 'Course added', description: `${formatCourseCode(normalizedPending)} was added to the first available semester in ${selectedPlan.name}.`, kind: 'saved' }
        : { title: 'Course could not be added', description: 'Choose a semester in the plan and try again.', kind: 'error' });
      clearPendingCourse();
    });
  }, [pendingCourseId, planData, plannedCodes, router, selectedPlan]);


  const addCourseToSemester = async (semesterNumber: number, course: Course) => {
    const item: SemesterItem = {
      kind: 'course',
      code: course.code,
      name: course.name,
      credits: course.credits,
      semester: semesterNumber,
      completed: false,
    };
    return addItemToSemester(semesterNumber, item);
  };

  const addCourseByCodeToSemester = async (semesterNumber: number, code: string, name: string, credits: number) => {
    if (!selectedPlan || !planData) return;

    try {
      const allCourses = catalogCourses;
      const norm = (c: string) => c.replace(/[^0-9]/g, '');
      const updatedPlanData = JSON.parse(JSON.stringify(planData)) as DegreePlanData;
      if (!Array.isArray(updatedPlanData.semesters)) updatedPlanData.semesters = [];
      if (!updatedPlanData.semesters.some(s => s.number === semesterNumber)) {
        const previousSemester = [...updatedPlanData.semesters]
          .filter(s => s.number < semesterNumber)
          .sort((a, b) => b.number - a.number)[0];
        const lastSemester = [...updatedPlanData.semesters].sort((a, b) => compareSemesters(a.name, b.name)).at(-1);
        updatedPlanData.semesters.push({
          number: semesterNumber,
          name: getNextSemesterName(previousSemester?.name || lastSemester?.name),
          courses: [],
          totalCredits: 0,
        });
      }

      const plannedNorms = new Set((updatedPlanData.semesters || []).flatMap(s => (s.courses || []).map(c => c.kind === 'course' ? norm(c.code) : norm((c as any).selectedCourseCode || ''))).filter(Boolean));
      const completedNorms = new Set((userPrereqInfo || []).map(u => norm(u.course_code)));

      // Get all recursive prerequisites
      const recursivePrereqs = getRecursivePrerequisites(code, allCourses);
      const itemsToAdd: { code: string; semester: number }[] = [];

      // For each prerequisite, find if it's missing and where to put it
      let latestPrereqSem = 0;
      for (const pCode of recursivePrereqs) {
        const pNorm = norm(pCode);
        if (plannedNorms.has(pNorm) || completedNorms.has(pNorm)) {
          // Already in plan or completed, find which semester it's in to ensure topological order
          const existingSem = (planData.semesters || []).find(s => (s.courses || []).some(c => c.kind === 'course' && norm(c.code) === pNorm));
          if (existingSem) latestPrereqSem = Math.max(latestPrereqSem, existingSem.number);
          continue;
        }

        // Find earliest semester with space (< 6 courses) that is AFTER latestPrereqSem AND BEFORE semesterNumber
        let targetSem = -1;
        for (let sNum = latestPrereqSem + 1; sNum < semesterNumber; sNum++) {
          const sem = updatedPlanData.semesters.find(s => s.number === sNum);
          if (sem && (sem.courses || []).length < 6) {
            targetSem = sNum;
            break;
          }
        }

        // If no semester has space, just pick the earliest one (Semester 1 or latestPrereqSem + 1)
        if (targetSem === -1) {
          targetSem = Math.min(semesterNumber - 1, latestPrereqSem + 1);
        }
        targetSem = Math.max(1, targetSem);

        itemsToAdd.push({ code: pCode, semester: targetSem });
        plannedNorms.add(pNorm);
        latestPrereqSem = Math.max(latestPrereqSem, targetSem);
      }

      const itemsForPlan: SemesterItem[] = itemsToAdd.map(item => {
        const pCredits = getCourseCredits(item.code) || 3;
        return {
          kind: 'course',
          code: item.code,
          name: getCourseName(item.code),
          credits: pCredits,
          semester: item.semester,
          completed: false,
          isAutoInsertedPrerequisite: true
        };
      });

      itemsForPlan.push({ kind: 'course', code, name, credits, semester: semesterNumber, completed: false });

      for (const item of itemsForPlan) {
        const semester = updatedPlanData.semesters.find(s => s.number === item.semester);
        if (!semester) throw new Error(`Could not find semester ${item.semester} while adding prerequisite chain`);
        if (!Array.isArray(semester.courses)) semester.courses = [] as any;
        (semester.courses as any).push(item);
      }

      let completedTotal = 0;
      updatedPlanData.semesters.forEach(s => {
        s.totalCredits = (s.courses || []).reduce((sum: number, item: any) => {
          return sum + (item.kind === 'course' ? Number(item.credits || 0) : 0);
        }, 0);
        completedTotal += s.totalCredits || 0;
      });
      if (updatedPlanData.requirements) {
        updatedPlanData.requirements.completed = completedTotal;
      }

      await degreePlansApi.update(selectedPlan.id, { plan: updatedPlanData });
      setPlanData(updatedPlanData);

    } catch (error) {
      console.error('Error in recursive course add:', error);
      setPlanningFeedback({ title: 'Course chain could not be added', description: 'Try again, or add the course without its prerequisite chain.', kind: 'error' });
    }
  };

  const addCourseListByNameToSemester = async (semesterNumber: number, mappingName: string, label: string, extra?: { courseListId?: number; credits?: number }) => {
    const item: SemesterItem = { kind: 'course_list', mappingName, label, credits: extra?.credits || 3, completed: false, quantity: 1, ...(extra?.courseListId !== undefined ? { courseListId: extra.courseListId } : {}) } as any;
    await addItemToSemester(semesterNumber, item);
  };


  const calculateMedianCredits = (courseCodes: string[], map: Map<string, number>): number => {
    if (!courseCodes || courseCodes.length === 0) return 3;
    const credits = courseCodes.map(c => map.get(normalize(c)) || 3).sort((a, b) => a - b);
    const mid = Math.floor(credits.length / 2);
    const median = credits.length % 2 !== 0 ? credits[mid] : (credits[mid - 1] + credits[mid]) / 2;
    return Math.round(median);
  };

  const handleAddCourseListBlockToSemester = async (
    block: { mappingName: string; courseListId: number; label: string },
    semesterNumber: number
  ) => {
    if (!selectedPlan || !planData) return;

    // Calculate median credits
    const courseListData = degreeCourseListsDirect.get(block.mappingName);
    const classes = courseListData?.classes || [];
    const medianCredits = calculateMedianCredits(classes, creditsMap);

    const item: SemesterItem = {
      kind: 'course_list',
      mappingName: block.mappingName,
      courseListId: block.courseListId,
      label: block.label,
      quantity: 1,
      credits: medianCredits,
      semester: semesterNumber,
      completed: false,
    };
    await addItemToSemester(semesterNumber, item);
  };

  // Smart distribution of course lists across semesters

  // Build remaining codes list from selected degrees and user prerequisites
  const [expandedPools, setExpandedPools] = useState<Map<string, string[]>>(new Map());
  useEffect(() => {
    const run = async () => {
      if (!planData?.selectedDegreeIds || planData.selectedDegreeIds.length === 0) {
        setAsyncRemainingBlocks([]);
        setExpandedPools(new Map());
        setSelectedDegreeRequirements([]);
        setDegreeProgress(null);
        return;
      }

      const uniqueDegreeIds = Array.from(new Set(planData.selectedDegreeIds));
      const allDegreeRequirements: any[] = [];
      const namesToExpand = new Set<string>();

      for (const degreeId of uniqueDegreeIds) {
        try {
          const degreeData = await loadDegreeRequirementsById(degreeId);

          if (degreeData) {
            const requirementsArray = Array.isArray(degreeData) ? degreeData : (degreeData as any)?.requirements || [];
            const degreeName = availableDegrees.find(d => d.ID === degreeId)?.Name || `Degree #${degreeId}`;
            setDegreeRequirementsCache((prev) => {
              if (prev.has(degreeId)) return prev;
              const next = new Map(prev);
              next.set(degreeId, degreeData as DegreeData);
              return next;
            });

            // Annotate requirements with degree info
            const annotate = (reqs: any[]): any[] => {
              return reqs.map((r: any) => ({
                ...r,
                _degreeId: degreeId,
                _degreeName: degreeName,
                requirements: Array.isArray(r.requirements) ? annotate(r.requirements) : r.requirements
              }));
            };

            const annotatedReqs = annotate(requirementsArray);
            allDegreeRequirements.push({
              degreeId,
              degreeName,
              requirements: annotatedReqs
            });

            // Collect course lists to expand
            const walkNames = (reqs: any[]) => {
              for (const r of reqs) {
                if (Array.isArray(r.course_pool)) {
                  for (const entry of r.course_pool) {
                    if (!isLikelyCourseCode(entry) && degreeCourseListsDirect.has(entry)) {
                      namesToExpand.add(entry);
                    }
                  }
                }
                if (Array.isArray(r.requirements)) walkNames(r.requirements);
              }
            };
            walkNames(requirementsArray);
          } else {
            allDegreeRequirements.push({ degreeId, degreeName: availableDegrees.find(d => d.ID === degreeId)?.Name || `Degree #${degreeId}`, requirements: [], coverageUnavailable: true });
          }
        } catch (e) {
          console.error('Failed to load degree requirements', degreeId, e);
          allDegreeRequirements.push({ degreeId, degreeName: availableDegrees.find(d => d.ID === degreeId)?.Name || `Degree #${degreeId}`, requirements: [], coverageUnavailable: true });
        }
      }

      // Expand mapping names to courses
      const pools = new Map<string, string[]>();
      for (const name of namesToExpand) {
        const data = degreeCourseListsDirect.get(name);
        if (data && data.classes && data.classes.length > 0) {
          pools.set(name, data.classes);
        }
      }
      setExpandedPools(pools);
      setSelectedDegreeRequirements(allDegreeRequirements);
      setListsLoadedVersion(v => v + 1);
    };
    run();
  }, [planData?.selectedDegreeIds, userPrereqInfo, degreeCourseListsDirect]);

  // Calculate overall progress when dependencies change
  useEffect(() => {
    if (selectedDegreeRequirements.length === 0) return;

    const context = createCalculationContext(
      userPrereqInfo,
      expandedPools,
      creditsMap,
      {
        plannedCodes,
        plannedCourseListBlocks,
        courseListCache,
        degreeCourseListsDirect,
        courseNameMap
      }
    );

    // For multiple degrees, we'll store a map of progress objects
    const progressMap = new Map<number, any>();

    selectedDegreeRequirements.forEach(degReq => {
      const degree = availableDegrees.find(d => d.ID === degReq.degreeId);
      if (degree && degReq.requirements?.length) {
        const progress = calculateDegreeProgressService(
          degree,
          degReq.requirements,
          context
        );
        progressMap.set(degReq.degreeId, progress);
      }
    });

    setDegreeProgress(progressMap);
  }, [selectedDegreeRequirements, expandedPools, userPrereqInfo, plannedCodes, listsLoadedVersion]);

  const mergedRequirements = useMemo(() => {
    return selectedDegreeRequirements.flatMap(dr => dr.requirements || []);
  }, [selectedDegreeRequirements]);

  const [asyncRemainingBlocks, setAsyncRemainingBlocks] = useState<{
    mappingName: string; courseListId: number; label: string
  }[]>([]);

  const getCourseName = (code: string): string => {
    const announced = announcedCourses[code.replace(/[^0-9]/g, '') as keyof typeof announcedCourses];
    if (announced) return announced.fullTitle;
    const formats = [code, code.replace(/^0+/, ''), `01${code}`, `0${code}`];
    for (const f of formats) {
      const name = courseNameMap.get(f);
      if (name) return name;
    }
    return code;
  };

  // Count course list blocks added to the plan by mapping name

  // Helper: get course credits from parsed courses (similar to degree-search)
  const getCourseCredits = (courseCode: string): number => {
    const announced = announcedCourses[courseCode.replace(/[^0-9]/g, '') as keyof typeof announcedCourses];
    if (announced) return announced.credits;
    return DegreeRequirementService.getCourseCredits(courseCode, creditsMap, userPrereqInfo);
  };

  // Helper: median credits for a course list (based on catalog)
  const getMedianCreditsForCourseList = (listName: string): number => {
    // Use direct mapping instead of ID-based lookup
    const data = degreeCourseListsDirect.get(listName);
    if (!data || !data.classes || data.classes.length === 0) {
      return 3; // Default to 3 credits if unknown
    }

    // Calculate median credits from the courses in the list
    const credits: number[] = [];
    data.classes.forEach(courseCode => {
      const courseCredits = creditsMap.get(normalize(String(courseCode)));
      if (courseCredits) {
        credits.push(courseCredits);
      }
    });

    if (credits.length === 0) return 3; // Default

    credits.sort((a, b) => a - b);
    const mid = Math.floor(credits.length / 2);
    return credits.length % 2 === 0
      ? (credits[mid - 1] + credits[mid]) / 2
      : credits[mid];
  };

  // Create a hash for user data and planned courses for cache invalidation (memoized)


  // Find alternative courses that can satisfy the same requirements as a given course

  // Check if a course is a lab and find its main course
  const getLabMainCourse = (courseCode: string): string | null => {
    const code = String(courseCode).replace(/[^0-9]/g, '');
    const allCourses = catalogCourses;
    const course = allCourses.find(c => c.baseId === code);

    if (!course) return null;

    const name = (course.courseName || '').toLowerCase();
    // Check if it's a lab
    if (name.includes('lab') || name.includes('laboratory')) {
      // Try to find the main course (usually same department, similar number)
      const dept = code.slice(0, 5); // First 5 digits (school:dept)
      const mainCourse = allCourses.find(c => {
        const cCode = String(c.baseId).replace(/[^0-9]/g, '');
        const cName = (c.courseName || '').toLowerCase();
        return cCode.startsWith(dept) &&
          !cName.includes('lab') &&
          !cName.includes('laboratory') &&
          Math.abs(parseInt(cCode.slice(5)) - parseInt(code.slice(5))) < 10;
      });

      return mainCourse ? mainCourse.baseId : null;
    }

    return null;
  };

  // Helper function to check course prerequisites
  const getCoursePrereqStatus = (code: string, completedCoursesForCheck = completedRutgersCourses): { ok: boolean; missing?: string[] } => {
    // code may be in forms like '01:640:151' or digits; normalize to digits
    const digits = String(code).replace(/[^0-9]/g, '');
    const allCourses = catalogCourses;
    const match = allCourses.find(c => c.baseId === digits);
    if (!match || !match.prerequisites) return { ok: true };
    const result = checkPrerequisites(match, completedCoursesForCheck as any);
    return { ok: result.isSatisfied, missing: result.missingPrerequisites };
  };

  // Validate prerequisites and lab-semester matching
  const validatePlanIssues = (): Array<{ type: 'prerequisite' | 'lab_semester'; course: string; issue: string; suggestion?: string }> => {
    const issues: Array<{ type: 'prerequisite' | 'lab_semester'; course: string; issue: string; suggestion?: string }> = [];

    if (!planData?.semesters) return issues;

    const allCourses = catalogCourses;
    const semesterCourses = new Map<number, Set<string>>();

    // Build semester course map
    planData.semesters.forEach(sem => {
      const codes = new Set<string>();
      (sem.courses || []).forEach((item: any) => {
        if (item.kind === 'course') {
          codes.add(normalize(String(item.code)));
        }
      });
      semesterCourses.set(sem.number, codes);
    });

    // Check prerequisites
    planData.semesters.forEach(sem => {
      (sem.courses || []).forEach((item: any) => {
        if (item.kind === 'course') {
          const priorSemesterCourses: any[] = [];
          for (let i = 1; i < sem.number; i++) {
            const priorCodes = semesterCourses.get(i) || new Set<string>();
            priorCodes.forEach(priorCode => {
              priorSemesterCourses.push({
                baseId: priorCode,
                id: priorCode,
                courseName: priorCode,
                fullTitle: priorCode,
                department: `${priorCode.slice(0, 2)}:${priorCode.slice(2, 5)}`,
                class_number: priorCode.slice(5),
                credits: 0,
                campus: 'NB',
                meetingTimes: [],
                instructors: [],
                section: 'PLAN',
                subjectNotes: '',
                coreRequirements: [],
                prerequisites: undefined
              });
            });
          }

          const status = getCoursePrereqStatus(item.code, [...completedRutgersCourses, ...priorSemesterCourses]);
          if (!status.ok && status.missing) {
            if (status.missing.length > 0) {
              issues.push({
                type: 'prerequisite',
                course: item.code,
                issue: `Missing prerequisites: ${status.missing.join(', ')}`,
                suggestion: `Move to a later semester or add prerequisites to earlier semesters`
              });
            }
          }

          // Check lab-semester matching
          const mainCourse = getLabMainCourse(item.code);
          if (mainCourse) {
            const mainCode = normalize(mainCourse);
            const mainInSameSemester = semesterCourses.get(sem.number)?.has(mainCode);
            if (!mainInSameSemester) {
              issues.push({
                type: 'lab_semester',
                course: item.code,
                issue: `Lab course should be in same semester as main course ${mainCourse}`,
                suggestion: `Move lab to same semester as ${mainCourse} or move ${mainCourse} to this semester`
              });
            }
          } else {
            // Check if this is a main course with a lab
            const course = allCourses.find(c => c.baseId === normalize(item.code));
            if (course) {
              const name = (course.courseName || '').toLowerCase();
              if (!name.includes('lab') && !name.includes('laboratory')) {
                // Look for corresponding lab
                const dept = String(item.code).replace(/[^0-9]/g, '').slice(0, 5);
                const labCourse = allCourses.find(c => {
                  const cCode = String(c.baseId).replace(/[^0-9]/g, '');
                  const cName = (c.courseName || '').toLowerCase();
                  return cCode.startsWith(dept) &&
                    (cName.includes('lab') || cName.includes('laboratory')) &&
                    Math.abs(parseInt(cCode.slice(5)) - parseInt(String(item.code).replace(/[^0-9]/g, '').slice(5))) < 10;
                });

                if (labCourse) {
                  const labCode = normalize(labCourse.baseId);
                  const labInSameSemester = semesterCourses.get(sem.number)?.has(labCode);
                  if (!labInSameSemester) {
                    // Check if lab is planned at all
                    let labPlanned = false;
                    for (const [, codes] of semesterCourses) {
                      if (codes.has(labCode)) {
                        labPlanned = true;
                        issues.push({
                          type: 'lab_semester',
                          course: item.code,
                          issue: `Main course should be in same semester as lab ${labCourse.baseId}`,
                          suggestion: `Move ${labCourse.baseId} to this semester or move this course to lab's semester`
                        });
                        break;
                      }
                    }
                    if (!labPlanned) {
                      issues.push({
                        type: 'lab_semester',
                        course: item.code,
                        issue: `Consider adding lab course ${labCourse.baseId} to this semester`,
                        suggestion: `Add ${labCourse.baseId} to same semester`
                      });
                    }
                  }
                }
              }
            }
          }
        }
      });
    });

    return issues;
  };

  // Calculate minimum courses needed

  // Find which requirements a course satisfies

  // Calculate progress for a single requirement (uses unified service)

  // Calculate overall degree progress (adapted from degree-search with planned courses support)
  interface DegreeProgress {
    degreeId?: number;
    degreeName?: string;
    overallProgress: number;
    requirementProgress: number;
    creditProgress: number;
    requirementsProgress: Array<{
      requirementId: string;
      title?: string;
      completed: boolean;
      progress: number;
      completedProgress: number;
      plannedProgress: number;
      creditsCompleted?: number;
      creditsPlanned?: number;
      neededCourses?: number;
      neededCredits?: number;
      remainingCourses?: number;
      remainingCredits?: number;
      displayCreditsNeeded?: number;
      displayCreditsCompleted?: number;
    }>;
    completedCourses: string[];
    remainingCourses: string[];
    totalCreditsNeeded: number;
    totalCreditsCompleted: number;
    totalCreditsPlanned: number;
  }

  const calculateDegreeProgress = (requirements: any, degree: Degree, poolsOverride?: Map<string, string[]>): DegreeProgress => {
    const context = createCalculationContext(
      userPrereqInfo,
      poolsOverride || expandedPools,
      creditsMap,
      {
        plannedCodes,
        plannedCourseListBlocks,
        courseListCache,
        degreeCourseListsDirect
      }
    );

    return calculateDegreeProgressService(
      degree,
      requirements,
      context,
      {
        includeMinimumCourses: false, // degree-planning doesn't show minimum courses
        poolsOverride
      }
    );
  };

  const buildPoolsForRequirements = (requirements: any): Map<string, string[]> => {
    const pools = new Map<string, string[]>();
    const reqArray = Array.isArray(requirements) ? requirements : (requirements as any)?.requirements || [];
    const walk = (reqs: any[]) => {
      reqs.forEach((req) => {
        (req.course_pool || []).forEach((entry: string) => {
          if (!isLikelyCourseCode(entry)) {
            const listData = degreeCourseListsDirect.get(entry);
            if (listData?.classes?.length) pools.set(entry, listData.classes);
          }
        });
        if (Array.isArray(req.requirements)) walk(req.requirements);
      });
    };
    walk(reqArray);
    return pools;
  };

  const estimateRemainingClasses = (progress: DegreeProgress | null): number | null => {
    if (!progress) return null;
    if (Array.isArray(progress.remainingCourses) && progress.remainingCourses.length > 0) {
      return progress.remainingCourses.length;
    }
    const estimated = (progress.requirementsProgress || []).reduce((sum, req: any) => {
      if (req.completed) return sum;
      if (typeof req.remainingCourses === 'number' && req.remainingCourses > 0) return sum + req.remainingCourses;
      if (typeof req.neededCourses === 'number' && req.neededCourses > 0) {
        const current = (req.completedCourses?.length || 0) + (req.plannedCourses?.length || 0);
        return sum + Math.max(0, req.neededCourses - current);
      }
      return sum;
    }, 0);
    return estimated || null;
  };

  const importDegreeRows = useMemo(() => {
    return filteredDegrees
      .filter(d => !SCHOOL_DEGREE_IDS.includes(d.ID))
      .map((degree) => {
        const degreeReq = degreeRequirementsCache.get(degree.ID);
        let progress: DegreeProgress | null = null;
        if (degreeReq) {
          try {
            progress = calculateDegreeProgress(degreeReq, degree, buildPoolsForRequirements(degreeReq));
          } catch (error) {
            console.error(`Error calculating progress for degree ${degree.ID}:`, error);
          }
        }
        const progressPercentage = progress ? Math.round(progress.overallProgress) : 0;
        const classesLeft = estimateRemainingClasses(progress);
        return { degree, progressPercentage, classesLeft, hasProgress: Boolean(progress) };
      })
      .sort((a, b) => {
        if (b.progressPercentage !== a.progressPercentage) return b.progressPercentage - a.progressPercentage;
        const aLeft = a.classesLeft ?? Number.POSITIVE_INFINITY;
        const bLeft = b.classesLeft ?? Number.POSITIVE_INFINITY;
        if (aLeft !== bLeft) return aLeft - bLeft;
        return (a.degree.Name || '').localeCompare(b.degree.Name || '');
      });
  }, [degreeRequirementsCache, filteredDegrees, userPrereqInfo, plannedCodes, expandedPools, degreeCourseListsDirect]);

  useEffect(() => {
    if (!showDegreeImportModal) return;
    const degreesToLoad = filteredDegrees
      .filter(d => !SCHOOL_DEGREE_IDS.includes(d.ID) && !degreeRequirementsCache.has(d.ID))
      .slice(0, 40);
    if (degreesToLoad.length === 0) return;

    let cancelled = false;
    const run = async () => {
      const loaded = new Map<number, DegreeData>();
      await Promise.all(degreesToLoad.map(async (degree) => {
        try {
          const degreeData = await loadDegreeRequirementsById(degree.ID);
          if (degreeData) loaded.set(degree.ID, degreeData as DegreeData);
        } catch (error) {
          console.error('Failed to load degree requirements for import modal', degree.ID, error);
        }
      }));
      if (!cancelled && loaded.size > 0) {
        setDegreeRequirementsCache((prev) => {
          const next = new Map(prev);
          loaded.forEach((value, key) => next.set(key, value));
          return next;
        });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [showDegreeImportModal, filteredDegrees, degreeRequirementsCache]);


  const removeCourseFromSemester = async (semesterNumber: number, courseIndex: number) => {
    if (!selectedPlan || !planData) return;

    const semester = planData.semesters.find(s => s.number === semesterNumber);
    if (!semester || !semester.courses[courseIndex]) return;

    const removedItem = semester.courses[courseIndex];
    const removedCredits = removedItem.kind === 'course' ? (removedItem.credits || 0) : 0;
    const previousPlanData = planData;
    const updatedPlanData = {
      ...planData,
      semesters: planData.semesters.map(s => s.number === semesterNumber ? {
        ...s,
        courses: s.courses.filter((_, index) => index !== courseIndex),
        totalCredits: s.totalCredits - removedCredits,
      } : s),
      requirements: {
        ...planData.requirements,
        completed: planData.requirements.completed - removedCredits,
      },
    };
    const optimisticSnapshot = JSON.stringify(updatedPlanData);

    setPlanData(updatedPlanData);

    // Sync with backend in the background
    try {
      await degreePlansApi.update(selectedPlan.id, { plan: JSON.parse(optimisticSnapshot) });
    } catch (error) {
      console.error('Error removing course:', error);
      // A newer edit may have changed this object in place or replaced it.
      // Restore only the exact optimistic state; never overwrite newer edits.
      setPlanData(current => current === updatedPlanData && JSON.stringify(current) === optimisticSnapshot
        ? previousPlanData
        : current);
      // Show error message to user
      setModalMessage('Failed to remove course. Please try again.');
      setShowModal(true);
    }
  };


  const clearAllCourses = async () => {
    if (!selectedPlan || !planData) return;
    if (!confirm('Are you sure you want to clear the entire plan? This will remove all semesters and courses and cannot be undone.')) {
      return;
    }

    try {
      const updated = JSON.parse(JSON.stringify(planData)) as DegreePlanData;
      updated.semesters = [];
      updated.requirements.completed = 0;

      await degreePlansApi.update(selectedPlan.id, { plan: updated });
      setPlanData(updated);
    } catch (error) {
      console.error('Error clearing plan:', error);
      setPlanningFeedback({ title: 'Plan could not be cleared', description: 'Please try clearing the plan again.', kind: 'error' });
    }
  };

  const handleAutoFillPlan = async () => {
    if (!selectedPlan || !planData || !mergedRequirements.length) {
      setPlanningFeedback({ title: 'Add requirements before auto-fill', description: 'Select a degree for this plan, then try auto-fill again.', kind: 'stale' });
      return;
    }

    try {
      setIsAutofilling(true);

      // Collect completed course codes (normalized)
      const completedCourses = (userPrereqInfo || []).map((u: any) => normalize(String(u.course_code)));

      debugDegreePlanning('[handleAutoFillPlan] Merged requirements count:', mergedRequirements.length);
      debugDegreePlanning('[handleAutoFillPlan] Current start:', CURRENT_START);

      // Fix: Normalize start season to Fall/Spring for the engine
      const engineStartSeason = (CURRENT_START.season === 'Fall' || CURRENT_START.season === 'Spring')
        ? CURRENT_START.season
        : (CURRENT_START.season === 'Winter' ? 'Spring' : 'Fall');

      const result = generateAutoFillPlan(mergedRequirements, {
        maxCreditsPerSemester: 18,
        minCreditsPerSemester: 12,
        startSeason: engineStartSeason,
        startYear: CURRENT_START.year,
        completedCourses,
        expandedPools,
        creditsMap,
        courseNameMap,
        degreeCourseListsDirect,
        catalogCourses,
      });
      debugDegreePlanning('[Auto-Fill] Stats:', result.stats);

      // The engine drafts a whole plan; the proposal is only what that draft would add to this one.
      const proposed: ProposedTermInput[] = result.semesters.map((s) => ({ name: s.name, courses: s.courses }));
      if (!diffProposal(planData.semesters || [], proposed, new Set(completedCourses)).length) {
        setPlanningFeedback({ title: 'Nothing new to suggest', description: 'Auto-fill found no courses beyond what is already earned or planned for the captured requirements. This does not establish completion.', kind: 'stale' });
        return;
      }
      setProposedTerms(proposed);
    } catch (error: any) {
      console.error('Error auto-filling plan:', error);
      const errorMessage = error?.message || 'Unknown error';
      setPlanningFeedback({ title: 'Auto-fill could not complete', description: `${errorMessage}. Check the requirements and try again.`, kind: 'error' });
    } finally {
      setIsAutofilling(false);
    }
  };

  const discardProposal = useCallback(() => setProposedTerms(null), []);

  const handleAcceptProposal = async (proposal: ProposalTerm[], accept: 'all' | string[]) => {
    if (!selectedPlan || !planData) return;
    try {
      setIsAcceptingProposal(true);
      const updated = JSON.parse(JSON.stringify(planData)) as DegreePlanData;
      updated.semesters = applyProposal(planData.semesters || [], proposal, accept);
      if (updated.requirements) {
        updated.requirements.completed = updated.semesters.reduce((sum, s) => sum + (s.totalCredits || 0), 0);
      }
      await degreePlansApi.update(selectedPlan.id, { plan: updated });
      setPlanData(updated);
      if (accept === 'all') setProposedTerms(null);
    } catch (error) {
      console.error('Error accepting auto-fill proposal:', error);
      setPlanningFeedback({ title: 'Suggestions could not be added', description: 'Your plan was not changed. Try accepting again.', kind: 'error' });
    } finally {
      setIsAcceptingProposal(false);
    }
  };

  const handleDeleteSemester = async (semesterNumber: number) => {
    if (!selectedPlan || !planData) return;
    const semester = planData.semesters.find(s => s.number === semesterNumber);
    if (!semester) return;

    if (!confirm(`Are you sure you want to delete ${semester.name}? All courses in it will be removed.`)) {
      return;
    }

    try {
      const updated = JSON.parse(JSON.stringify(planData)) as DegreePlanData;
      updated.semesters = updated.semesters.filter(s => s.number !== semesterNumber);

      let total = 0;
      updated.semesters.forEach(s => {
        (s.courses || []).forEach((c: any) => {
          total += (c.credits || 0);
        });
      });
      updated.requirements.completed = total;

      await degreePlansApi.update(selectedPlan.id, { plan: updated });
      setPlanData(updated);
    } catch (err) {
      console.error(err);
    }
  };


  const handleAddCustomSemester = async () => {
    if (!selectedPlan || !planData) return;
    try {
      const updated = JSON.parse(JSON.stringify(planData)) as DegreePlanData;
      const newName = `${seasonToAdd} ${yearToAdd}`;

      if (updated.semesters.find(s => s.name === newName)) {
        alert(`${newName} already exists in the plan.`);
        return;
      }

      const newNumber = updated.semesters.length > 0
        ? Math.max(...updated.semesters.map(s => s.number)) + 1
        : 1;

      updated.semesters.push({
        number: newNumber,
        name: newName,
        courses: [],
        totalCredits: 0
      });

      // Maintain chronological order based on new comparison logic
      updated.semesters.sort((a, b) => compareSemesters(a.name, b.name));

      await degreePlansApi.update(selectedPlan.id, { plan: updated });
      setPlanData(updated);
      setShowSeasonSelectorModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDropCourse = async (toSemesterNumber: number, fromSemesterNumber: number, itemIndex: number, toIndex?: number) => {
    if (!selectedPlan || !planData) return;
    try {
      const updated = JSON.parse(JSON.stringify(planData)) as DegreePlanData;
      const from = updated.semesters.find(s => s.number === fromSemesterNumber);
      const to = updated.semesters.find(s => s.number === toSemesterNumber);
      if (!from || !to) return;
      const [moved] = (from.courses as any).splice(itemIndex, 1);

      // Check availability warning for Summer/Winter (catalog flags from classes.json)
      const parsed = parseSemesterName(to.name);
      const rawId = String(moved.code ?? moved.baseId ?? '');
      const flags = getCatalogSeasonFlags(rawId);
      if (parsed && flags && (parsed.season === 'Summer' || parsed.season === 'Winter')) {
        const ok = parsed.season === 'Summer' ? flags.offeredSummer : flags.offeredWinter;
        if (!ok) {
          const label = moved.name || moved.courseName || moved.label || rawId;
          alert(`Warning: ${label} is typically not offered in the ${parsed.season}. You can still add it, but it may not be available when registering.`);
        }
      }

      if (!Array.isArray((to as any).courses)) (to as any).courses = [];
      if (toIndex !== undefined && toIndex >= 0 && toIndex <= (to as any).courses.length) {
        if (toSemesterNumber === fromSemesterNumber && itemIndex < toIndex) {
          // Because we removed an item BEFORE toIndex, the new target index is toIndex - 1
          (to as any).courses.splice(toIndex - 1, 0, moved);
        } else {
          (to as any).courses.splice(toIndex, 0, moved);
        }
      } else {
        (to as any).courses.push(moved);
      }
      const sumCredits = (s: any) => (s.courses || []).reduce((acc: number, it: any) => acc + (it.credits || 0), 0);
      from.totalCredits = sumCredits(from);
      to.totalCredits = sumCredits(to);
      await degreePlansApi.update(selectedPlan.id, { plan: updated });
      setPlanData(updated);
    } catch (err) {
      console.error(err);
    }
  };

  // Build a minimal set of completed courses from unified prerequisite info for checking status
  const completedRutgersCourses = useMemo(() => {
    return (userPrereqInfo || []).map((u: any) => {
      const digits = String(u.course_code || '').replace(/[^0-9]/g, '');
      const dept = `${digits.slice(0, 2)}:${digits.slice(2, 5)}`;
      const num = digits.slice(5);
      return {
        baseId: digits,
        id: digits,
        courseName: u.name || digits,
        fullTitle: u.name || digits,
        department: dept,
        class_number: num,
        credits: Number(u.credits || 0),
        campus: 'NB',
        meetingTimes: [],
        instructors: [],
        section: 'HIST',
        subjectNotes: '',
        coreRequirements: [],
        prerequisites: undefined
      } as RutgersCourse;
    });
  }, [userPrereqInfo]);

  // Degree credit counts: include previously taken classes (non-overrides carry real credits) + planned items
  const completedDegreeCredits = useMemo(() => {
    return (completedRutgersCourses || []).reduce((sum, c) => sum + (typeof c.credits === 'string' ? parseFloat(c.credits) : (c.credits || 0)), 0);
  }, [completedRutgersCourses]);

  const semestersByYear = useMemo(() => {
    if (!planData?.semesters) return [];

    // Sort all semesters chronologically
    const sortedSemesters = [...planData.semesters].sort((a, b) => compareSemesters(a.name, b.name));

    const years: { name: string, semesters: Semester[] }[] = [];

    // Group semesters by their calendar year
    const yearGroups = new Map<number, Semester[]>();
    for (const sem of sortedSemesters) {
      const parsed = parseSemesterName(sem.name);
      const y = parsed ? parsed.year : CURRENT_START.year;
      if (!yearGroups.has(y)) yearGroups.set(y, []);
      yearGroups.get(y)!.push(sem);
    }

    const sortedYears = Array.from(yearGroups.keys()).sort((a, b) => a - b);
    for (let i = 0; i < sortedYears.length; i++) {
      years.push({
        name: `${sortedYears[i]}`,
        semesters: yearGroups.get(sortedYears[i])!
      });
    }

    return years;
  }, [planData?.semesters]);

  const issues = useMemo(() => validatePlanIssues(), [validatePlanIssues, planData]);
  const plannerSemesters = useMemo(() => semestersByYear.flatMap((year) => year.semesters), [semestersByYear]);


  const totalDisplayedCompletedCredits = useMemo(() => {
    let planned = 0;
    (planData?.semesters || []).forEach(s => {
      (s.courses || []).forEach((c: any) => {
        if (c.kind === 'course' && c.completed) planned += (c.credits || 0);
      });
    });
    return completedDegreeCredits + planned;
  }, [planData?.semesters, completedDegreeCredits]);

  const totalDisplayedPlannedCredits = useMemo(() => {
    let planned = 0;
    (planData?.semesters || []).forEach(s => {
      (s.courses || []).forEach((item: any) => {
        if (item.kind === 'course' && !item.completed) {
          planned += item.credits || 0;
        } else if (item.kind === 'course_list') {
          const listName = item.mappingName || item.courseListName;
          if (listName) {
            planned += getMedianCreditsForCourseList(listName) * (item.quantity || 1);
          }
        }
      });
    });
    return planned;
  }, [planData?.semesters, degreeCourseListsDirect]);

  const plannerSummary = useMemo(() => {
    const totalCredits = planData?.totalCredits || 0;
    const completionPercent = totalCredits > 0
      ? Math.min(100, Math.round((totalDisplayedCompletedCredits / totalCredits) * 100))
      : 0;
    const plannedPercent = totalCredits > 0
      ? Math.min(100, Math.round(((totalDisplayedCompletedCredits + totalDisplayedPlannedCredits) / totalCredits) * 100))
      : 0;

    return {
      completionPercent,
      plannedPercent,
    };
  }, [planData, totalDisplayedCompletedCredits, totalDisplayedPlannedCredits]);

  // Display-only gap between the plan's target and what is earned or placed; it never feeds degree calculations.
  const creditsLeftToPlace = useMemo(() => {
    const target = planData?.totalCredits;
    if (!Number.isFinite(target) || !target || target <= 0) return null;
    return Math.max(0, Math.round((target - totalDisplayedCompletedCredits - totalDisplayedPlannedCredits) * 100) / 100);
  }, [planData?.totalCredits, totalDisplayedCompletedCredits, totalDisplayedPlannedCredits]);

  const targetPrograms = useMemo(() => {
    return (planData?.selectedDegreeIds || [])
      .map((degreeId: number) => availableDegrees.find(d => d.ID === degreeId))
      .filter((degree): degree is Degree => Boolean(degree));
  }, [availableDegrees, planData?.selectedDegreeIds]);

  // Helper: Calculate total credits in a semester (including course lists)

  // Autofill function with opt-in debug tracing and iterative prerequisite handling

  // previous class handlers removed

  const handleSelectCourseForSemester = (course: GroupedCourse) => {
    if (!targetSemester) return;

    // Check availability warning for Summer/Winter (catalog flags from classes.json)
    const semester = planData?.semesters.find(s => s.number === targetSemester);
    if (semester) {
      const parsed = parseSemesterName(semester.name);
      const flags = getCatalogSeasonFlags(String(course.id));
      if (parsed && flags && (parsed.season === 'Summer' || parsed.season === 'Winter')) {
        const ok = parsed.season === 'Summer' ? flags.offeredSummer : flags.offeredWinter;
        if (!ok) {
          alert(`Warning: ${course.name} is typically not offered in the ${parsed.season}. You can still add it, but it may not be available when registering.`);
        }
      }
    }

    const newCourse: Course = {
      code: course.id,
      name: course.name,
      credits: Number(course.sections?.[0]?.credits ?? '3'),
      semester: targetSemester,
      completed: false,
    };

    addCourseToSemester(targetSemester, newCourse);
    setShowAddToSemesterModal(false);
    setTargetSemester(null);
  };

  const handleSharePlan = async () => {
    if (!selectedPlan) return;
    setTogglingShare(true);
    try {
      if (!selectedPlan.is_shareable) await degreePlansApi.update(selectedPlan.id, { is_shareable: true });
      const refreshed = await degreePlansApi.get(selectedPlan.id) as DegreePlan;
      if (!refreshed.share_token) throw new Error('Share link is unavailable');
      setSelectedPlan(refreshed);
      const link = `${window.location.origin}/degree-planning/shared/${refreshed.share_token}`;
      setShareLink(link);
      setShareDialogOpen(true);
    } catch (error) {
      console.error('Could not prepare degree plan sharing:', error);
      setPlanningFeedback({ title: 'Share link unavailable', description: 'Your plan could not be shared right now. Please try again.', kind: 'error' });
    } finally {
      setTogglingShare(false);
    }
  };

  const disablePlanSharing = async () => {
    if (!selectedPlan) return;
    setTogglingShare(true);
    try {
      await degreePlansApi.update(selectedPlan.id, { is_shareable: false });
      setSelectedPlan({ ...selectedPlan, is_shareable: false });
      setShareLink(null);
      setShareDialogOpen(false);
      setPlanningFeedback({ title: 'Sharing turned off', description: 'The previous share link can no longer open this plan.', kind: 'saved' });
    } catch (error) {
      console.error('Could not turn off degree plan sharing:', error);
      setPlanningFeedback({ title: 'Could not turn off sharing', description: 'Please try again.', kind: 'error' });
    } finally {
      setTogglingShare(false);
    }
  };
  // The plan is a dense working board: keep its repeated decision surfaces solid
  // so requirement evidence and drag targets stay readable at every zoom level.
  const planningCardClassName = 'border border-[var(--ss-border)] bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-sm dark:shadow-none';
  const planningHoverCardClassName = 'border border-[var(--ss-border)] bg-[var(--ss-surface)] text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)]';

  if (isLoading) {
    return <PageLoading title="Loading your degree plans" description="Restoring saved plans, course progress, and requirement data." />;
  }

  if (loadingError) {
    return (
      <div className={`flex items-center justify-center bg-[var(--ss-canvas)] px-6 ${embedded ? 'min-h-96' : 'min-h-full'}`}>
        <div className="w-full max-w-lg rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] p-8 text-[var(--ss-text)] shadow-e2">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-red-50 p-3 text-red-600 dark:bg-red-950/50 dark:text-red-300">
              <ExclamationTriangleIcon className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Degree Planning is temporarily unavailable</h1>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{loadingError}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setIsLoading(true);
                    loadUserData();
                  }}
                  className="rounded-control bg-[var(--ss-brand)] px-4 py-2.5 text-sm font-semibold text-[var(--ss-brand-contrast)] transition-colors hover:bg-[var(--ss-brand-strong)]"
                >
                  Retry
                </button>
                <button
                  onClick={() => router.push('/support')}
                  className="rounded-control border border-[var(--ss-border)] px-4 py-2.5 text-sm font-medium text-[var(--ss-text-muted)] transition-colors hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)]"
                >
                  Get support
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'min-h-full'} bg-transparent text-[var(--ss-text)]`}>

      <div className={`mx-auto max-w-[1860px] ${embedded ? 'py-1' : 'px-6 py-8 2xl:px-10'}`}>
        {planningFeedback ? (
          <div className="mb-5">
            <AcademicDecisionFeedback
              academicObject={planningFeedback.title}
              state={planningFeedback.kind}
              description={planningFeedback.description}
              nextAction={<button type="button" onClick={() => setPlanningFeedback(null)} className="min-h-11 rounded-lg border border-current px-3 text-sm font-semibold hover:bg-black/5 dark:hover:bg-white/10">Continue planning</button>}
            />
          </div>
        ) : null}
        {/* Header: one compact bar for the plan, its programs and its commands, then the credit summary. */}
        {selectedPlan && planData && (
          <div className="mb-5 flex flex-col gap-3 border-b border-[var(--ss-border)] pb-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <div className="relative plan-selector-container min-w-0">
                <button
                  onClick={() => setShowPlanSelector(!showPlanSelector)}
                  aria-expanded={showPlanSelector}
                  aria-controls="degree-plan-switcher"
                  aria-label={`Active plan: ${selectedPlan.name}. Switch plan`}
                  className="flex min-h-12 md:min-h-10 max-w-full items-center gap-2 rounded-control px-1 text-left transition-colors hover:text-[var(--ss-brand)] motion-reduce:transition-none lg:max-w-[40rem]"
                >
                  <span className="truncate text-lg font-bold text-[var(--ss-text)] md:text-xl" title={selectedPlan.name}>{selectedPlan.name}</span>
                  <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-[var(--ss-text-muted)] transition-transform duration-150 motion-reduce:transition-none ${showPlanSelector ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                    {showPlanSelector && (
                      <div id="degree-plan-switcher" className={`absolute top-full left-0 z-50 mt-2 max-h-96 w-80 overflow-y-auto rounded-card shadow-e2 ${planningCardClassName}`}>
                        {degreePlans.map((plan) => (
                          <div key={plan.id} className={`border-b border-[var(--ss-border)] last:border-0 ${selectedPlan.id === plan.id ? 'bg-[var(--ss-surface-subtle)]' : ''}`}>
                            <div className="flex items-center gap-2 px-3 py-2">
                              <button
                                type="button"
                                onClick={() => { setShowPlanSelector(false); handleSelectPlan(plan); }}
                                aria-current={selectedPlan.id === plan.id ? 'true' : undefined}
                                className="min-h-12 min-w-0 flex-1 rounded-control px-1 text-left hover:bg-[var(--ss-surface-subtle)]"
                              >
                                <span className="block truncate font-bold text-[var(--ss-text)]">{plan.name}</span>
                                <span className="mt-1 block text-xs text-[var(--ss-text-muted)]">{plan.plan?.major || 'No major set'}</span>
                              </button>
                              <div className="flex shrink-0 flex-col items-end gap-1">
                                {selectedPlan.id === plan.id ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ss-positive)]"><CheckIcon className="h-4 w-4" aria-hidden="true" />Current</span> : null}
                                {primaryPlanId === plan.id ? <span className="text-[10px] font-semibold text-[var(--ss-text-muted)]">Primary on this device</span> : (
                                  <button type="button" onClick={() => {
                                    setPrimaryPlanId(plan.id);
                                    if (primaryPlanStorageKey) { try { window.localStorage.setItem(primaryPlanStorageKey, String(plan.id)); } catch { /* storage is optional */ } }
                                  }} className="min-h-12 sm:min-h-8 rounded-control px-2 text-xs font-semibold text-[var(--ss-brand)] hover:bg-[var(--ss-surface-subtle)]">Set primary on this device</button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setShowPlanSelector(false);
                            setShowCreateForm(true);
                          }}
                          className="flex min-h-12 w-full items-center gap-2 border-t border-[var(--ss-border)] px-4 py-3 text-left text-sm font-semibold text-[var(--ss-brand)] transition-colors hover:bg-[var(--ss-surface-subtle)] motion-reduce:transition-none"
                        >
                          <PlusIcon className="h-4 w-4" aria-hidden="true" />
                          New plan
                        </button>
                      </div>
                    )}
              </div>
              {planData.targetGraduation ? <button type="button" onClick={() => setIsEditingPlan(true)} className="min-h-12 rounded-control px-1 text-sm text-[var(--ss-text-muted)] hover:text-[var(--ss-text)] md:min-h-0">Graduating <b className="font-semibold text-[var(--ss-text)]">{planData.targetGraduation}</b></button> : null}
              <span className="hidden flex-1 md:block" aria-hidden="true" />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={togglingShare}
                  onClick={handleSharePlan}
                  className={`flex min-h-12 md:min-h-10 items-center gap-1.5 rounded-control px-3 text-sm font-medium text-[var(--ss-text-muted)] transition-colors hover:text-[var(--ss-text)] disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none ${planningHoverCardClassName}`}
                >
                  <ShareIcon className="h-4 w-4" aria-hidden="true" />
                  Share
                </button>
                <button
                  onClick={handleAutoFillPlan}
                  disabled={isAutofilling}
                  aria-busy={isAutofilling}
                  className="flex min-h-12 md:min-h-10 items-center gap-2 rounded-control border border-[color-mix(in_srgb,var(--ss-brand)_45%,var(--ss-border))] px-3.5 text-sm font-semibold text-[var(--ss-brand)] transition-colors hover:bg-[var(--ss-brand-bg)] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
                  title="Suggest courses for the requirements that are still open. Nothing is saved until you accept."
                >
                  {isAutofilling ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current motion-reduce:animate-none" aria-hidden="true" />
                  ) : (
                    <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                  {isAutofilling ? 'Drafting…' : 'Auto-fill the rest'}
                </button>
                <details className="relative group/options">
                  <summary
                    aria-label="More plan options"
                    className={`flex min-h-12 cursor-pointer list-none items-center gap-2 rounded-control px-3 text-sm font-medium text-[var(--ss-text-muted)] transition-colors hover:text-[var(--ss-text)] motion-reduce:transition-none md:min-h-10 ${planningHoverCardClassName}`}
                  >
                    <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                    <span className="hidden sm:inline">More</span>
                  </summary>
                  <div className={`absolute right-0 top-[calc(100%+0.5rem)] z-40 w-64 rounded-card p-2 ${planningCardClassName}`}>
                    <button
                      type="button"
                      onClick={() => setIsEditingPlan(true)}
                      className="flex min-h-12 w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm font-medium text-[var(--ss-text)] transition-colors hover:bg-[var(--ss-surface-subtle)] motion-reduce:transition-none"
                    >
                      Edit plan details
                    </button>
                    <label className="flex min-h-12 cursor-pointer items-center justify-between gap-4 rounded-control px-3 py-2 hover:bg-[var(--ss-surface-subtle)]">
                      <span className="text-sm font-medium text-[var(--ss-text)]">Summer/Winter terms</span>
                      <span className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={showSummerWinterOnly}
                          onChange={(e) => setShowSummerWinterOnly(e.target.checked)}
                        />
                        <span className="h-5 w-9 rounded-full bg-[var(--ss-border)] after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-[var(--ss-border)] after:bg-white after:transition-transform after:duration-150 peer-checked:bg-[var(--ss-brand)] peer-checked:after:translate-x-4 peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--ss-focus)] motion-reduce:after:transition-none" />
                      </span>
                    </label>
                    <div className="my-1 border-t border-[var(--ss-border)]" role="separator" />
                    <button
                      type="button"
                      onClick={clearAllCourses}
                      className="flex min-h-12 w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm font-medium text-[var(--ss-critical)] transition-colors hover:bg-[var(--ss-critical-bg)] motion-reduce:transition-none"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      Clear all planned classes
                    </button>
                  </div>
                </details>
              </div>
            </div>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-6">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <span className="sr-only">Programs in this plan</span>
                <DegreeTargets
                  targets={targetPrograms}
                  colorFor={(degree) => {
                    const programIndex = selectedDegreeRequirements.findIndex((program) => program.degreeId === degree.ID);
                    return programColor(programIndex >= 0 ? programIndex : 0);
                  }}
                  onView={(degree) => router.push(`/explore?view=degrees&degree=${degree.ID}`)}
                  onRemove={async (degree) => {
                    const updated = (planData.selectedDegreeIds || []).filter((id: number) => id !== degree.ID);
                    const newPlanData = { ...planData, selectedDegreeIds: updated };
                    setPlanData(newPlanData);
                    if (selectedPlan) {
                      await degreePlansApi.update(selectedPlan.id, { plan: newPlanData });
                    }
                  }}
                  onAdd={() => setShowDegreeImportModal(true)}
                />
              </div>
              <div className="flex min-w-0 shrink-0 flex-col gap-1.5 lg:w-[26rem]">
                <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm text-[var(--ss-text-muted)]">
                  <span><b className="tabular-nums text-[var(--ss-text)]">{totalDisplayedCompletedCredits}</b> earned · <b className="tabular-nums text-[var(--ss-text)]">{totalDisplayedPlannedCredits}</b> planned{creditsLeftToPlace !== null ? <> · <b className="tabular-nums text-[var(--ss-text)]">{creditsLeftToPlace}</b> to place</> : null}</span>
                  {creditsLeftToPlace !== null
                    ? <span className="whitespace-nowrap text-xs">of {planData.totalCredits} cr</span>
                    : <button type="button" onClick={() => setIsEditingPlan(true)} className="text-xs font-semibold text-[var(--ss-text-muted)] underline-offset-2 hover:text-[var(--ss-text)] hover:underline">Set credit target</button>}
                </p>
                {/* Without a target the bar still shows the earned/planned split instead of sitting empty. */}
                <div className="flex h-1.5 overflow-hidden rounded-full bg-[var(--ss-surface-subtle)] ring-1 ring-inset ring-[var(--ss-border)]" role="img" aria-label={`${totalDisplayedCompletedCredits} earned credits, ${totalDisplayedPlannedCredits} planned credits${creditsLeftToPlace !== null ? `, ${planData.totalCredits} target credits` : ', target credits not set'}`}>
                  <div className="h-full bg-[var(--ss-positive)] transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${creditsLeftToPlace !== null ? plannerSummary.completionPercent : (100 * totalDisplayedCompletedCredits) / Math.max(1, totalDisplayedCompletedCredits + totalDisplayedPlannedCredits)}%` } as React.CSSProperties} />
                  <div className="planner-slot-planned h-full transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${creditsLeftToPlace !== null ? Math.max(0, plannerSummary.plannedPercent - plannerSummary.completionPercent) : (100 * totalDisplayedPlannedCredits) / Math.max(1, totalDisplayedCompletedCredits + totalDisplayedPlannedCredits)}%` } as React.CSSProperties} />
                </div>
              </div>
            </div>
          </div>
        )}

        {!selectedPlan ? (
          <div className="rounded-[2rem] border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-[#121212] md:p-16">
            <BookOpenIcon className="mx-auto mb-6 h-16 w-16 text-gray-400" />
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white">Start with a degree plan</h3>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-gray-600 dark:text-gray-300">
              Create a plan first, then attach degree requirements and start placing courses into semesters. The planner keeps the semester canvas, requirement coverage, and prerequisite warnings in one workflow.
            </p>
            <div className="mx-auto mt-6 grid max-w-3xl gap-3 text-left sm:grid-cols-3">
              <div className="rounded-2xl bg-gray-50 p-4 dark:bg-[#0F0F0F]">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Step 1</div>
                <div className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Create the plan shell</div>
              </div>
              <div className="rounded-2xl bg-gray-50 p-4 dark:bg-[#0F0F0F]">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Step 2</div>
                <div className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Attach majors, minors, and school requirements</div>
              </div>
              <div className="rounded-2xl bg-gray-50 p-4 dark:bg-[#0F0F0F]">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Step 3</div>
                <div className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Fill semesters and review warnings</div>
              </div>
            </div>
            <button
              onClick={() => {
                setShowCreateForm(true);
                setNewPlanMajor(onboardingMajors.join(', '));
              }}
              className="mx-auto mt-8 flex items-center space-x-3 rounded-2xl bg-[#E31C25] px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-red-600/20 transition-all hover:bg-[#FF2E38]"
            >
              <PlusIcon className="h-6 w-6" />
              <span>Create your first plan</span>
            </button>
          </div>
        ) : planData ? (
          <PlannerWorkspace
            key={selectedPlan.id}
            openTransferOptions={openTransferOptions}
            completionStorageKey={user?.id ? `scarlet-sync:completion:v1:${user.id}:${selectedPlan.id}` : undefined}
            semesters={plannerSemesters}
            selectedDegreeRequirements={selectedDegreeRequirements}
            degreeProgress={degreeProgress}
            expandedPools={expandedPools}
            degreeCourseListsDirect={degreeCourseListsDirect}
            userPrereqInfo={userPrereqInfo}
            creditsMap={creditsMap}
            earnedCredits={totalDisplayedCompletedCredits}
            issues={issues}
            getCourseName={getCourseName}
            onAddCourseByCodeToSemester={addCourseByCodeToSemester}
            onAddCourseListByNameToSemester={addCourseListByNameToSemester}
            onDropCourse={handleDropCourse}
            onCourseListClick={(item, semesterNumber, itemIndex) => setSelectingCourseList({ item, semesterNumber, itemIndex })}
            onAddFromSearch={(semesterNumber) => {
              setTargetSemester(semesterNumber);
              setShowAddToSemesterModal(true);
            }}
            onAddSemester={() => {
              const allSems = planData?.semesters || [];
              if (allSems.length > 0) {
                const last = allSems[allSems.length - 1];
                const next = getNextSemesterName(last.name);
                const nm = parseSemesterName(next);
                if (nm) { setSeasonToAdd(nm.season); setYearToAdd(nm.year); }
              } else {
                setSeasonToAdd('Fall');
                setYearToAdd(new Date().getFullYear());
              }
              setShowSeasonSelectorModal(true);
            }}
            onDeleteSemester={handleDeleteSemester}
            onDegreeImport={() => setShowDegreeImportModal(true)}
            onRemoveSemesterItem={removeCourseFromSemester}
            proposedTerms={proposedTerms}
            proposalBusy={isAcceptingProposal}
            onAcceptProposal={handleAcceptProposal}
            onDiscardProposal={discardProposal}
            userCourseLists={userCourseLists}
            userCourseListsReady={userCourseListsReady}
            onAskCourseAI={(_intent, courseCode, placement) => {
              const params = new URLSearchParams();
              params.set('redirectedFrom', 'degree-planning');
              params.set('prompt', `Explain how ${courseCode} fits into my degree plan${placement?.semesterName ? ` for ${placement.semesterName}` : ''}.`);
              router.push(`/ai-assistant?${params.toString()}`);
            }}
          />
        ) : (
          <div className="rounded-[2rem] border border-gray-200 bg-white p-16 text-center shadow-sm dark:border-white/10 dark:bg-[#121212]">
            <LoadingBubbles className="mx-auto mb-6" />
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {planSelectionLoading ? 'Switching plans…' : 'Hydrating academic data…'}
            </p>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Loading semesters, attached requirement sets, and prerequisite context for this workspace.
            </p>
          </div>
        )}
      </div>

      {/* New Plan Modal */}
      <DegreePlanningModal
        isOpen={showCreateForm}
        title="New Degree Plan"
        size="md"
        onClose={() => {
          setShowCreateForm(false);
          setNewPlanName('');
          setNewPlanMajor('');
          setNewPlanDegreeIds([]);
          setNewPlanTargetGraduation('Spring 2028');
          setNewPlanDescription('');
        }}
      >
            <p className="mb-4 text-sm text-content-muted">Create a plan to track your academic progress.</p>
            <div className="space-y-4">
              {/* Plan Name */}
              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Plan Name</label>
                <input
                  type="text"
                  placeholder="e.g. My 4-Year Plan"
                  value={newPlanName}
                  onChange={(e) => setNewPlanName(e.target.value)}
                  autoFocus
                  className="w-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#E31C25]/50 transition-all text-sm font-medium"
                />
              </div>

              {/* Degrees Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Degrees</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newPlanDegreeIds.map((degreeId) => {
                    const degree = availableDegrees.find(d => d.ID === degreeId);
                    if (!degree) return null;
                    return (
                      <div
                        key={degreeId}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl"
                      >
                        <AcademicCapIcon className="h-3.5 w-3.5 text-gray-400" />
                        <AcademicProgramBadge
                          name={degree.Name}
                          degreeCode={degree["Degree Code"]}
                          compact
                        />
                        <button
                          onClick={() => setNewPlanDegreeIds(prev => prev.filter(id => id !== degreeId))}
                          className="min-h-12 min-w-12 hover:text-red-500 transition-colors"
                          aria-label={`Remove ${degree.Name}`}
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => setShowDegreeImportModal(true)}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-2 transition-colors hover:border-[#E31C25] hover:bg-[#E31C25]/10 dark:border-white/20 dark:bg-white/5 group"
                >
                  <PlusIcon className="h-4 w-4 text-gray-400 group-hover:text-[#E31C25]" />
                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400 group-hover:text-[#E31C25] uppercase tracking-widest">Add Degree</span>
                </button>
              </div>

              {/* Target Graduation */}
              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Target Graduation</label>
                <input
                  type="text"
                  placeholder="e.g. Spring 2028"
                  value={newPlanTargetGraduation}
                  onChange={(e) => setNewPlanTargetGraduation(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#E31C25]/50 transition-all text-sm font-medium"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Description (Optional)</label>
                <textarea
                  placeholder="What's this plan for?"
                  value={newPlanDescription}
                  onChange={(e) => setNewPlanDescription(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-[#E31C25]/50 transition-all text-sm font-medium resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewPlanName('');
                  setNewPlanMajor('');
                  setNewPlanDegreeIds([]);
                  setNewPlanTargetGraduation('Spring 2028');
                  setNewPlanDescription('');
                }}
                className="min-h-12 flex-1 rounded-2xl border border-gray-200 py-3 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePlan}
                disabled={isCreating || !newPlanName.trim()}
                className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#E31C25] py-3 text-sm font-black uppercase tracking-wider text-white transition-colors hover:bg-[#c9191f] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Plan'
                )}
              </button>
            </div>
      </DegreePlanningModal>

      {/* Course List Selection Modal */}
      {
        selectingCourseList && (() => {
          const allCourses = degreeCourseListsDirect.get(selectingCourseList.item.mappingName)?.classes
            || userCourseLists.find((list) => userListMappingName(list.id) === selectingCourseList.item.mappingName)?.classes
            || [];
          const searchLower = courseListSearch.toLowerCase();
          const filteredCourses = courseListSearch.trim()
            ? allCourses.filter((code: string) => {
                const norm = normalize(code);
                const name = getCourseName(code) ?? '';
                return formatCourseCode(code).toLowerCase().includes(searchLower) || name.toLowerCase().includes(searchLower) || norm.includes(searchLower);
              })
            : allCourses;

          return (
            <DegreePlanningModal
              isOpen
              title="Select a Course"
              size="lg"
              onClose={() => { setSelectingCourseList(null); setCourseListSearch(''); }}
            >
                <p className="text-xs font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400">{selectingCourseList.item.label}</p>
                <p className="mt-0.5 text-xs text-content-muted">{allCourses.length} eligible courses</p>

                {/* Search */}
                <div className="mt-4 border-b border-gray-100 pb-3 dark:border-white/10">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by course code or name..."
                      value={courseListSearch}
                      onChange={(e) => setCourseListSearch(e.target.value)}
                      autoFocus
                      className="min-h-12 w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-4 text-sm text-gray-900 outline-none transition-colors placeholder-gray-400 focus:ring-2 focus:ring-purple-500/50 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder-gray-500 dark:focus:ring-purple-400/50"
                    />
                  </div>
                </div>

                {/* Course list */}
                <div className="max-h-[52vh] space-y-2 overflow-y-auto py-4 custom-scrollbar">
                  {filteredCourses.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-8">No courses match your search.</p>
                  ) : (
                    filteredCourses.map((courseCode: string, idx: number) => {
                      const norm = normalize(courseCode);
                      const courseName = getCourseName(courseCode);
                      const isCompleted = (userPrereqInfo || []).some((u: any) => normalize(u.course_code) === norm);
                      const isPlannedInOtherSemester = plannedCodes.includes(norm) && (!selectingCourseList.item.selectedCourseCode || normalize(selectingCourseList.item.selectedCourseCode) !== norm);
                      const isSelected = selectingCourseList.item.selectedCourseCode && normalize(selectingCourseList.item.selectedCourseCode) === norm;

                      return (
                        <button
                          key={idx}
                          onClick={async () => {
                            if (isCompleted || isPlannedInOtherSemester) return;
                            const updatedPlanData = { ...planData } as DegreePlanData;
                            const semester = updatedPlanData.semesters.find(s => s.number === selectingCourseList.semesterNumber);
                            if (semester && semester.courses) {
                              const listItem = semester.courses[selectingCourseList.itemIndex];
                              if (listItem && listItem.kind === 'course_list') {
                                listItem.selectedCourseCode = courseCode;
                                await degreePlansApi.update(selectedPlan!.id, { plan: updatedPlanData });
                                setPlanData(updatedPlanData);
                              }
                            }
                            setSelectingCourseList(null); setCourseListSearch('');
                          }}
                          disabled={isCompleted || isPlannedInOtherSemester}
                          className={`min-h-12 w-full text-left p-3.5 rounded-2xl border-2 flex items-center gap-3 transition-all
                            ${isSelected ? 'bg-purple-50 dark:bg-purple-500/10 border-purple-400 dark:border-purple-500' :
                              isCompleted ? 'bg-green-50 dark:bg-green-500/5 border-green-200 dark:border-green-500/20 opacity-70 cursor-not-allowed' :
                              isPlannedInOtherSemester ? 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/5 opacity-50 cursor-not-allowed' :
                              'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-[0_0_12px_rgba(168,85,247,0.12)] cursor-pointer'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-black tracking-wider text-sm ${isCompleted ? 'text-green-700 dark:text-green-400' : isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-gray-900 dark:text-white'}`}>
                                {`${courseName || 'Untitled course'}, ${formatCourseCode(courseCode)}`}
                              </span>
                              {isCompleted && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400">Done</span>}
                              {isPlannedInOtherSemester && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400">Placed</span>}
                              {isSelected && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400">Selected</span>}
                            </div>
                            <span className={`text-xs mt-0.5 block truncate ${isCompleted ? 'text-green-600/80 dark:text-green-500/70' : 'text-gray-500 dark:text-gray-400'}`}>
                              {`Course code: ${formatCourseCode(courseCode)}`}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer: unselect */}
                {selectingCourseList.item.selectedCourseCode && (
                  <div className="border-t border-gray-100 pt-4 dark:border-white/10">
                    <button
                      onClick={async () => {
                        const updatedPlanData = { ...planData } as DegreePlanData;
                        const semester = updatedPlanData.semesters.find(s => s.number === selectingCourseList.semesterNumber);
                        if (semester && semester.courses) {
                          const listItem = semester.courses[selectingCourseList.itemIndex];
                          if (listItem && listItem.kind === 'course_list') {
                            delete listItem.selectedCourseCode;
                            await degreePlansApi.update(selectedPlan!.id, { plan: updatedPlanData });
                            setPlanData(updatedPlanData);
                          }
                        }
                        setSelectingCourseList(null); setCourseListSearch('');
                      }}
                      className="flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-red-50 py-3 text-xs font-black uppercase tracking-widest text-red-600 transition-colors hover:bg-red-100 hover:text-red-700 dark:bg-red-500/5 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300 group"
                    >
                      Unselect Picked Course
                      <XMarkIcon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                )}
            </DegreePlanningModal>
          );
        })()
      }

      {/* Add to Semester Modal */}
      <DegreePlanningModal isOpen={showAddToSemesterModal} onClose={() => setShowAddToSemesterModal(false)} title="Add Course to Semester" size="lg">
              <CourseSearch
                onSelectCourse={handleSelectCourseForSemester}
                selectedCourses={dummySelectedCourses}
                onRemoveCourse={() => { }}
                onAddCoreBlock={() => { }}
                selectedCoreBlocks={dummyCoreBlocks}
                completedCourses={completedRutgersCourses}
                maxResults={5}
              />
              {/* Quick-add requirement blocks (course lists) like scheduling page */}
              {asyncRemainingBlocks && asyncRemainingBlocks.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Requirement Blocks</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                    {asyncRemainingBlocks.map((b) => (
                      <button
                        key={`${b.mappingName}-${b.courseListId}`}
                        onClick={() => targetSemester && handleAddCourseListBlockToSemester(b, targetSemester)}
                        className="min-h-12 w-full rounded border p-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/60"
                      >
                        <div className="font-medium">{b.label}</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">Requirement Block • {b.mappingName}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowAddToSemesterModal(false)}
                  className="min-h-12 px-4 py-2 text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
                >
                  Cancel
                </button>
              </div>
      </DegreePlanningModal>

      {/* Season Selector Modal */}
      <DegreePlanningModal isOpen={showSeasonSelectorModal} onClose={() => setShowSeasonSelectorModal(false)} title="Add Semester" size="sm">
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Season</label>
                  <select
                    value={seasonToAdd}
                    onChange={(e) => setSeasonToAdd(e.target.value as any)}
                    className="w-full bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-white/10 rounded-2xl px-5 py-4 text-gray-900 dark:text-white font-bold outline-none focus:ring-2 focus:ring-[#E31C25]/50 transition-all appearance-none cursor-pointer"
                    aria-label="Select Season"
                  >
                    <option value="Spring">Spring</option>
                    <option value="Summer">Summer</option>
                    <option value="Fall">Fall</option>
                    <option value="Winter">Winter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Year</label>
                  <input
                    type="number"
                    value={yearToAdd}
                    onChange={(e) => setYearToAdd(parseInt(e.target.value) || new Date().getFullYear())}
                    className="w-full bg-gray-50 dark:bg-[#1A1A1A] border border-gray-200 dark:border-white/10 rounded-2xl px-5 py-4 text-gray-900 dark:text-white font-bold outline-none focus:ring-2 focus:ring-[#E31C25]/50 transition-all"
                    aria-label="Enter Year"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setShowSeasonSelectorModal(false)}
                  className="min-h-12 flex-1 rounded-2xl bg-gray-100 py-4 text-xs font-black uppercase tracking-widest text-gray-900 transition-colors hover:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCustomSemester}
                  className="min-h-12 flex-1 rounded-2xl bg-[#E31C25] py-4 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-[#FF2E38]"
                >
                  Add
                </button>
              </div>
      </DegreePlanningModal>

      {/* Edit Plan Modal */}
      {isEditingPlan && selectedPlan && planData && (
        <DegreePlanningModal
          isOpen
          title="Edit Plan"
          size="xl"
          onClose={() => {
            setIsEditingPlan(false);
            setEditedPlan(null);
          }}
        >
            <div className="space-y-6">
              {/* Plan Name */}
              <div>
                <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={editedPlan?.name || selectedPlan.name}
                  onChange={(e) => setEditedPlan({ ...(editedPlan || { name: selectedPlan.name, major: planData.major, targetGraduation: planData.targetGraduation }), name: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white focus:border-[#E31C25] focus:outline-none transition-colors"
                  placeholder="e.g., My 4-Year Plan"
                />
              </div>

              {/* Degrees Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Degrees
                </label>
                <div className="flex flex-wrap gap-2">
                  {(editedPlan?.selectedDegreeIds || planData?.selectedDegreeIds || []).map((degreeId: number) => {
                    const degree = availableDegrees.find(d => d.ID === degreeId);
                    if (!degree) return null;

                    return (
                      <div
                        key={degreeId}
                        className="group flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-white/10 rounded-lg hover:border-red-500/50 transition-all shadow-sm"
                      >
                        <AcademicCapIcon className="h-4 w-4 text-gray-400" />
                        <AcademicProgramBadge
                          name={degree.Name}
                          degreeCode={degree["Degree Code"]}
                          compact
                        />
                        <button
                          onClick={async () => {
                            setEditedPlan(prev => prev ? ({
                              ...prev,
                              selectedDegreeIds: (prev.selectedDegreeIds || planData?.selectedDegreeIds || []).filter((id: number) => id !== degreeId)
                            }) : null);
                          }}
                          className="min-h-12 min-w-12 opacity-0 transition-opacity group-hover:opacity-100"
                          aria-label={`Remove ${degree.Name}`}
                        >
                          <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    );
                  })}

                  <button
                    onClick={() => setShowDegreeImportModal(true)}
                    className="flex min-h-12 items-center gap-2 rounded-lg border border-dashed border-white/20 bg-[#0A0A0A] px-3 py-2 transition-colors hover:border-[#E31C25] hover:bg-[#E31C25]/10 group"
                  >
                    <PlusIcon className="h-4 w-4 text-gray-400 group-hover:text-[#E31C25]" />
                    <span className="text-sm font-medium text-gray-400 group-hover:text-[#E31C25]">Add Degree</span>
                  </button>
                </div>
              </div>

              {/* Target Graduation */}
              <div>
                <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Target Graduation
                </label>
                <input
                  type="text"
                  value={editedPlan?.targetGraduation || planData.targetGraduation}
                  onChange={(e) => setEditedPlan({ ...(editedPlan || { name: selectedPlan.name, major: planData.major, targetGraduation: planData.targetGraduation }), targetGraduation: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white focus:border-[#E31C25] focus:outline-none transition-colors"
                  placeholder="e.g., Spring 2028"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={editedPlan?.description || planData.description || ''}
                  onChange={(e) => setEditedPlan({ ...(editedPlan || { name: selectedPlan.name, major: planData.major, targetGraduation: planData.targetGraduation }), description: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white focus:border-[#E31C25] focus:outline-none transition-colors resize-none"
                  rows={3}
                  placeholder="Add a description for your plan..."
                />
              </div>

            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/10">
              <button
                onClick={() => {
                  setIsEditingPlan(false);
                  setEditedPlan(null);
                }}
                className="min-h-12 flex-1 rounded-xl border border-white/10 bg-[#0A0A0A] px-6 py-3 font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1A1A1A]"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (editedPlan) {
                    try {
                      const updatedPlanData = { ...planData, ...editedPlan };
                      await degreePlansApi.update(selectedPlan.id, {
                        plan: updatedPlanData,
                        name: editedPlan.name
                      });
                      setPlanData(updatedPlanData);
                      setSelectedPlan({ ...selectedPlan, name: editedPlan.name });
                      setIsEditingPlan(false);
                      setEditedPlan(null);
                    } catch (err) {
                      console.error(err);
                      setPlanningFeedback({ title: 'Plan changes could not be saved', description: 'Please review the edits and try saving again.', kind: 'error' });
                    }
                  } else {
                    setIsEditingPlan(false);
                  }
                }}
                className="min-h-12 flex-1 rounded-xl bg-[#E31C25] px-6 py-3 font-black uppercase tracking-wide text-white transition-colors hover:bg-[#FF2E38]"
              >
                Save Changes
              </button>
            </div>
        </DegreePlanningModal>
      )}

      {/* Degree Import Modal */}
      <DegreePlanningModal isOpen={showDegreeImportModal} onClose={() => setShowDegreeImportModal(false)} title="Import Degree Requirements" size="xl">
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Select a degree to import its requirements into your plan. This will create a new plan based on the official degree requirements.
                </p>
                <div>
                  <input
                    aria-label="Search degrees"
                    value={degreeSearchQuery}
                    onChange={(e) => setDegreeSearchQuery(e.target.value)}
                    placeholder="Search degrees by name or code..."
                    className="min-h-12 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-red-400 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {importDegreeRows.map(({ degree, progressPercentage, classesLeft, hasProgress }) => {
                    return (
                      <button
                        type="button"
                        key={degree.ID}
                        onClick={() => handleImportDegreeRequirements(degree.ID)}
                        className="min-h-12 w-full cursor-pointer rounded-lg bg-gray-100 p-3 text-left transition-colors hover:bg-gray-200 dark:bg-gray-700/50 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">{degree.Name}</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {degree["Degree Code"]} • {degree["Start Term"]}
                            </p>
                          </div>
                          <div className="ml-4 flex min-w-[150px] items-center justify-end gap-3 text-right">
                            <div>
                              <div className="text-lg font-semibold text-[#E31C25]">
                                {hasProgress ? `${progressPercentage}%` : '...'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">complete</div>
                            </div>
                            <div className="h-8 w-px bg-gray-200 dark:bg-gray-600" />
                            <div>
                              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {classesLeft ?? '-'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">classes left</div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {importDegreeRows.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-600">
                      No matching degrees.
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDegreeImportModal(false)}
                  className="min-h-12 px-4 py-2 text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
                >
                  Cancel
                </button>
              </div>
      </DegreePlanningModal>

      {/* School Requirements Import Modal */}
      <DegreePlanningModal isOpen={showSchoolRequirementsModal} onClose={() => setShowSchoolRequirementsModal(false)} title="Add School-Wide Requirements" size="xl">
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Select your school's core curriculum or special program requirements to add to your plan.
                </p>
                <div className="space-y-6 max-h-96 overflow-y-auto">
                  {(() => {
                    const { schools, honors } = categorizeSchools(availableDegrees);
                    return (
                      <>
                        {/* Schools Section */}
                        {schools.length > 0 && (
                          <div>
                            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                              <AcademicCapIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                              Schools & Programs
                            </h4>
                            <div className="space-y-3">
                              {schools.map((degree) => {
                                const IconComponent = getSchoolIcon(degree.Name);
                                return (
                                  <button
                                    type="button"
                                    key={degree.ID}
                                    onClick={() => handleImportDegreeRequirements(degree.ID)}
                                    className="flex min-h-12 w-full cursor-pointer items-center rounded-lg bg-gray-100 p-3 text-left transition-colors hover:bg-gray-200 dark:bg-gray-700/50 dark:hover:bg-gray-700"
                                  >
                                    <IconComponent className="w-5 h-5 mr-3 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                    <div className="flex-1">
                                      <h4 className="font-medium text-gray-900 dark:text-white">{degree.Name}</h4>
                                      <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {degree["Degree Code"]} • {degree["Start Term"]}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Honors Section */}
                        {honors.length > 0 && (
                          <div>
                            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                              <SparklesIcon className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
                              Honors Programs & Special Programs
                            </h4>
                            <div className="space-y-3">
                              {honors.map((degree) => {
                                const IconComponent = getSchoolIcon(degree.Name);
                                return (
                                  <button
                                    type="button"
                                    key={degree.ID}
                                    onClick={() => handleImportDegreeRequirements(degree.ID)}
                                    className="flex min-h-12 w-full cursor-pointer items-center rounded-lg bg-gray-100 p-3 text-left transition-colors hover:bg-gray-200 dark:bg-gray-700/50 dark:hover:bg-gray-700"
                                  >
                                    <IconComponent className="w-5 h-5 mr-3 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                    <div className="flex-1">
                                      <h4 className="font-medium text-gray-900 dark:text-white">{degree.Name}</h4>
                                      <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {degree["Degree Code"]} • {degree["Start Term"]}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowSchoolRequirementsModal(false)}
                  className="min-h-12 px-4 py-2 text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
                >
                  Cancel
                </button>
              </div>
      </DegreePlanningModal>

      <Modal isOpen={shareDialogOpen && Boolean(shareLink)} onClose={() => setShareDialogOpen(false)} title="Share degree plan" size="md">
        <div className="space-y-4">
          <p className="text-sm text-[var(--ss-text-muted)]">Anyone with this link can view the plan's name, major, semesters, and courses. They cannot edit it.</p>
          <label className="block text-sm font-medium text-[var(--ss-text)]">Share link
            <input className="ss-field mt-1" type="text" value={shareLink ?? ''} readOnly onFocus={(event) => event.target.select()} />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="ss-button bg-[var(--ss-brand-solid)] text-white" onClick={async () => {
              if (!shareLink) return;
              try {
                await navigator.clipboard.writeText(shareLink);
                setPlanningFeedback({ title: 'Link copied', description: 'You can send the read-only plan link to someone you choose.', kind: 'saved' });
              } catch {
                setPlanningFeedback({ title: 'Copy unavailable', description: 'Select the link in the field and copy it manually.', kind: 'stale' });
              }
            }}>Copy link</button>
            <button type="button" disabled={togglingShare} onClick={disablePlanSharing} className="ss-button border border-[var(--ss-border)] text-[var(--ss-critical)] disabled:opacity-50">Turn off sharing</button>
          </div>
        </div>
      </Modal>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Error"
        size="sm"
      >
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{modalMessage}</p>
          <button
            onClick={() => setShowModal(false)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Onboarding Modal */}
      <OnboardingModal isOpen={showOnboardingModal} onClose={closeOnboardingModal} />

      {/* Prerequisite Checker Prompt */}
      <PrerequisiteCheckerPrompt
        isOpen={showPrerequisitePrompt}
        onClose={() => setShowPrerequisitePrompt(false)}
      />
      <AssistantButton
        context={{
          skill: 'degree_planning',
          planId: selectedPlan?.id,
          planName: selectedPlan?.name,
          major: planData?.major,
          semesters: planData?.semesters,
          selectedDegreeIds: planData?.selectedDegreeIds,
        }}
      />
    </div>
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><DegreePlanningPage /></RouteSuspense>; }
